// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Linking, Modal, Platform, Pressable, ScrollView, StyleSheet, TextInput } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { Text, View } from '@/components/Themed';
import { KEYCLOAK_BASE } from '@/auth/oidc';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import appColors, { alpha } from '@/theme/colors';
import { COURSE_ADMIN_COUNTRIES, DEFAULT_COURSE_ADMIN_COUNTRY_CODE } from '@/features/course-admin/countries';
import {
  ALL_COUNTRIES,
  courseListParams,
  filterCourses,
  OSM_FEATURE_KINDS,
  pageOf,
  parseCourseListParams,
  type AnalysisFilter,
  type CourseListFilters,
  // Named for what it is: the counts the OSM import stored for a course.
  type OsmFeatureCounts as StoredOsmFeatureCounts,
} from '@/features/course-admin/courseListView';
import { CourseHoleMapModal } from '@/features/course-trace/CourseHoleMapModal';

function describeProgress(
  progress: ImportProgress,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  switch (progress.phase) {
    case 'authoritativeSource':
      return t('golfCourseAdminPage.progressAuthoritativeSource', { source: progress.source });
    case 'searching':
      return t('golfCourseAdminPage.progressSearching');
    case 'found':
      return progress.unnamedInCountry > 0
        ? t('golfCourseAdminPage.progressFoundWithUnnamed', {
            count: String(progress.totalFoundInCountry),
            unnamed: String(progress.unnamedInCountry),
          })
        : t('golfCourseAdminPage.progressFound', { count: String(progress.totalFoundInCountry) });
    case 'importing':
      return t('golfCourseAdminPage.progressImporting', { count: String(progress.total) });
    default:
      return '';
  }
}

type ImportedCourse = { courseId: string; name: string };

type ImportProgress =
  | { phase: 'authoritativeSource'; source: string }
  | { phase: 'searching' }
  | { phase: 'found'; totalFoundInCountry: number; unnamedInCountry: number }
  | { phase: 'importing'; total: number };

/**
 * What the country's own mapping agency did, when it has one. Absent for every country whose
 * best source is OpenStreetMap — see CourseImportSources on the BFF.
 */
type AuthoritativeSourceResult = {
  source: string;
  /** Null when the source was unavailable: the import still ran, from OSM alone. */
  result: { boundariesAttached: number; discovered: number } | null;
};

/** One selectable source in the import modal, with how much of it is already in. */
type ImportSourceOption = {
  key: string;
  displayName: string;
  /** Courses this source already contributed. Null when the lookup failed — unknown, not zero. */
  alreadyImported: number | null;
};

/** The modal's source checkboxes: what this country's import could run. */
type ImportOptionsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; sources: ImportSourceOption[] }
  | { status: 'error' };

type ImportState =
  | { status: 'idle' }
  | { status: 'importing'; progress: ImportProgress | null }
  | {
      status: 'done';
      importedCount: number;
      skippedCount: number;
      failedCount: number;
      /** OSM courses the search found with no name. They import as "Unnamed course <osmId>" and
       * stay disabled, so they need a human to rename them before anyone can find them. Kept in
       * the summary rather than only in the `found` progress line, which has long scrolled past
       * by the time a country import ends. */
      unnamedCount: number;
      imported: ImportedCourse[];
      authoritativeSource: AuthoritativeSourceResult | null;
    }
  | { status: 'error' };

/** The catalogue-wide OSM feature import: every course with an OSM identity but no OSM terrain
 * rows yet gets its geometry and terrain fetched from OpenStreetMap. Runs for minutes (Overpass
 * is rate-limited, one course a second at best), so progress carries the course being visited. */
/** What the club-details fetch is doing for one club. Keyed by club id rather than held once,
 *  because the action lives on the course card now: several rows on screen can belong to
 *  different clubs, and a single state would light up every one of them at once. */
type ClubEnrichmentState =
  | { status: 'idle' }
  | { status: 'running' }
  | {
      status: 'done';
      enriched: boolean;
      gotLogo: boolean;
      gotDescription: boolean;
      /** Why nothing happened. "no website" points at the club list; "http 403" points at the
       *  club's own server, and those need completely different fixes. */
      skippedReason: string | null;
    }
  | { status: 'error'; code: string | null };

type FeatureImportState =
  | { status: 'idle' }
  /** Asked; the ack has not come back yet. */
  | { status: 'running' }
  /** Handed to the batch fleet, which reads the country's whole OSM extract in one pass. */
  | { status: 'queued'; coursesRequested: number }
  | { status: 'error'; code: string | null };

/** One national OSM extract import, as geodata reports it. */
type ExtractJob = {
  id: string;
  countryCode: string;
  status: string;
  workerId: string | null;
  coursesRequested: number;
  coursesWithFeatures: number;
  coursesApplied: number;
  featuresWritten: number;
  featuresUpdated: number;
  featuresRemoved: number;
  featuresSkipped: number;
  durationSeconds: number | null;
  error: string | null;
  /**
   * What became of each course, grouped. Absent from a geodata that predates the field, in which
   * case the breakdown line is simply not drawn.
   */
  outcomes?: {
    updated: number;
    alreadyCurrent: number;
    nothingInOsm: number;
    blocked: number;
    failed: number;
    /** blocked + failed — the courses a re-run could still change. */
    remaining: number;
  } | null;
};

/** The extract runs the modal shows, and whether a worker is switched on to take one. */
type ExtractsState = { jobs: ExtractJob[]; workerOnline: boolean };

/** One course on one extract run: what became of it, and the matcher's word on why. */
type ExtractCourseOutcome = {
  courseId: string;
  courseName: string | null;
  outcome: string;
  elements: number;
  written: number;
  updated: number;
  removed: number;
  skipped: number;
  detail: string | null;
};

/** The per-course rows of the run the admin opened, loaded on demand. */
type ExtractCoursesState =
  | { status: 'idle' }
  | { status: 'loading'; jobId: string }
  | { status: 'loaded'; jobId: string; courses: ExtractCourseOutcome[] }
  | { status: 'error'; jobId: string };

/** i18n key per outcome geodata reports; an outcome newer than this list shows its raw name. */
const EXTRACT_OUTCOME_KEYS: Record<string, string> = {
  applied: 'extractOutcomeApplied',
  unchanged: 'extractOutcomeUnchanged',
  no_holes_mapped: 'extractOutcomeNoHolesMapped',
  no_elements: 'extractOutcomeNoElements',
  dropped_no_identity: 'extractOutcomeDroppedNoIdentity',
  dropped_no_position: 'extractOutcomeDroppedNoPosition',
  skipped_no_identity: 'extractOutcomeSkippedNoIdentity',
  not_run: 'extractOutcomeNotRun',
  failed: 'extractOutcomeFailed',
  requested: 'extractOutcomeRequested',
};

/** Rows grouped by outcome, largest group first: the "why" the job's totals leave out. */
const groupExtractCourses = (courses: ExtractCourseOutcome[]) => {
  const groups = new Map<string, ExtractCourseOutcome[]>();
  for (const course of courses) {
    groups.set(course.outcome, [...(groups.get(course.outcome) ?? []), course]);
  }
  return [...groups.entries()]
    .map(([outcome, rows]) => ({ outcome, courses: rows }))
    .sort((a, b) => b.courses.length - a.courses.length);
};

/** Running counts for the "Backfill missing analysis" sweep — courses whose analysisState is
 * neverRequested, each auto-matched against OSM if possible and always queued for satellite
 * analysis regardless. */
type BackfillCounts = { matched: number; triggeredWithoutMatch: number; failed: number };

type BackfillState =
  | { status: 'idle' }
  | ({ status: 'running' } & BackfillCounts)
  | ({ status: 'done' } & BackfillCounts)
  | { status: 'error' };

/**
 * The three visibilities an admin sets from here — the same set the course editor models.
 * `pending` is left out on purpose: it means "a user submitted this and it is waiting for
 * approval", which is something that happens to a course rather than something declared about one.
 */
const SETTABLE_VISIBILITIES = ['official', 'private', 'disabled'] as const;

type SettableVisibility = (typeof SETTABLE_VISIBILITIES)[number];

/**
 * The bulk "Set visibility" action. `done` carries both counts because a run can half-succeed —
 * the BFF patches each course on its own and reports which ones took.
 */
type VisibilityActionState =
  | { status: 'idle' }
  | { status: 'running' }
  | { status: 'done'; updated: number; failed: number }
  | { status: 'error' };

/**
 * Mirrors SatelliteAnalysisStatusEndpoints.DeriveState; null means the bulk lookup found nothing
 * for this id (geodata unreachable, or the request predates that lookup).
 *
 * `noGeodata` — no geo_courses row at all — is the one state that says the per-course actions
 * cannot work yet: analyze, flyover render and OSM refresh all resolve the row first and 404
 * without it. `null` is not the same claim: geodata did not answer, so nothing is known and the
 * buttons stay live.
 */
type AnalysisState =
  | 'disabled'
  | 'noGeodata'
  | 'neverRequested'
  | 'pending'
  | 'abandoned'
  | 'analysed'
  | null;

type CourseRow = {
  id: string;
  name: string;
  /** The club that owns the course. Absent from a BFF that predates the field. */
  clubId?: string;
  clubName: string;
  city: string;
  country: string;
  /**
   * The club's website, or null when no source we trust has stated one. Null renders as nothing —
   * a blank is honest where a placeholder link would not be.
   */
  clubWebsiteUrl?: string | null;
  /** Club coordinates — the catalogue has no per-course position. */
  lat: number;
  lon: number;
  visibility: string;
  par: number;
  holesCount: number;
  /**
   * How many tees the course has. Absent from a BFF that predates the field, which reads as 0 —
   * the same thing it means for a course that genuinely has none, and both are worth showing.
   */
  teeCount?: number;
  osmType: string | null;
  osmId: number | null;
  analysisState: AnalysisState;
  /** What the OSM import has stored for this course. All zero when nothing was imported. */
  osmFeatures: StoredOsmFeatureCounts;
  /**
   * The latest flyover job, or null when none was ever queued (or the lookup failed) —
   * so a null reads as "unknown", never as a false "not rendered". Optional so an older BFF
   * that does not send it renders the row without a flyover line at all.
   */
  flyover?: { latestStatus: string; renderedHoles: number; rendered: boolean } | null;
  /**
   * Holes with both a tee and a green — what a render would actually produce. 0 is a course with
   * no hole geometry to fly (nothing imported, nothing analysed), so the render would refuse;
   * null/undefined is "geodata did not say", which must leave the action offered.
   */
  flyableHoles?: number | null;
  /**
   * What Golf i Sverige's published scorecard contributed to this course, or null/undefined when
   * it contributed nothing — or when the BFF could not reach golf-courses-service to ask. Both
   * render as no line at all, never as "0 holes imported", which would claim an import happened
   * and found an empty card.
   */
  scorecardImport?: { importedAtUtc: string; holes: number; tees: number } | null;
  /**
   * What the most recent OSM extract import did to this course, or null/undefined when no run
   * has mentioned it — or when the lookup did not answer. Both render as no line at all.
   */
  osmExtract?: { outcome: string; detail: string | null; requestedAtUtc: string } | null;
};

const NO_OSM_FEATURES: StoredOsmFeatureCounts = { greens: 0, holes: 0, bunkers: 0, fairways: 0 };

/**
 * The import timestamp as a plain local date. Falls back to the raw value rather than rendering
 * "Invalid Date" if the BFF ever sends something unparseable — the point of the line is the
 * counts, and a date nobody can read should not swallow them.
 */
const formatImportDate = (value: string): string => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
};

/** Stable identity, so the filtered/paged memos don't recompute while the list is loading. */
const EMPTY_COURSES: CourseRow[] = [];

type CoursesState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'loaded'; totalCount: number; courses: CourseRow[] }
  | { status: 'error' };

/**
 * Per-row "Refresh OSM" state and, once done, the delta geodata reported.
 *
 * A failure carries the HTTP status because the reasons are not interchangeable and the admin
 * can only act on one of them: 404 is a course with no geodata row, 409 a course with no OSM
 * identity to refresh from, 502 an Overpass outage nobody here can fix. `null` is a request that
 * never got an answer at all.
 */
type OsmRefreshRowState =
  | { status: 'running' }
  | { status: 'failed'; httpStatus: number | null }
  | { status: 'done'; written: number; updated: number; removed: number; unchanged: number; skipped: number };

/**
 * Per-row state of an attended Golf i Sverige scorecard import.
 *
 * `done` with nothing written is an ordinary outcome, not a failure, and there are several of
 * them: the club page matched no course on this club, the page carried no readable card, every
 * row on it belongs to a person who entered it (CLAUDE.md §18 — an import never writes over a
 * human), or the import is switched off here. The service answers 200 for all of them, so what
 * happened is in `reason` and never in the status — which is why the counts alone must not be
 * reported as an import.
 */
type GisImportRowState =
  | { status: 'running' }
  | { status: 'failed'; httpStatus: number | null }
  | {
      status: 'done';
      holes: number;
      tees: number;
      /** The Golf i Sverige club page that was matched, when it got that far. */
      clubSlug: string | null;
      reason: string | null;
    };

/** Per-row "Start analyze" request state, separate from the course's own analysisState (that
 * comes from the last full list fetch; this is the in-flight request against one row). */
type AnalyzeRowState =
  | { status: 'running' }
  | { status: 'queued' }
  /** The BFF's courseAdmin.courses.analyze.* code — it already tells not_found from unavailable. */
  | { status: 'failed'; code?: string };

/** One flyover render job as geodata reports it (see FlyoverJobResponse). */
type FlyoverJob = {
  jobId: string;
  status: 'queued' | 'succeeded' | 'failed';
  requestedAt: string;
  finishedAt: string | null;
  workerId: string | null;
  renderedHoles: number | null;
  skippedHoles: number | null;
  error: string | null;
};

/**
 * flyableHoles is how many holes have both a tee and a green — what a render would actually
 * produce. Optional so an older geodata that does not send it cannot disable the button.
 */
type FlyoverStatus = { workerOnline: boolean; flyableHoles?: number; latestJob: FlyoverJob | null };

type FlyoverRequestState = 'idle' | 'requesting' | 'queued' | 'failed';

/**
 * What to tell the admin when a per-course action fails.
 *
 * These three actions fail for the same handful of reasons, and only some of them are the
 * admin's to fix — so each reason gets its own line rather than one "it failed" that covers a
 * missing geodata row, an Overpass outage and an unconfigured analyser alike.
 */
function osmRefreshFailure(httpStatus: number | null): { key: string; params?: Record<string, string> } {
  switch (httpStatus) {
    case 404:
      return { key: 'golfCourseAdminPage.actionNoGeodata' };
    case 409:
      return { key: 'golfCourseAdminPage.osmRefreshNoIdentity' };
    case 502:
      return { key: 'golfCourseAdminPage.osmRefreshOverpassSilent' };
    case null:
      return { key: 'golfCourseAdminPage.osmRefreshFailed' };
    default:
      return { key: 'golfCourseAdminPage.osmRefreshFailedStatus', params: { status: String(httpStatus) } };
  }
}

function analyzeFailureKey(code: string | undefined): string {
  switch (code) {
    // The request carries the row's position, and geodata creates its own record from that when
    // it has none — so a not_found here is the narrower complaint that no position was sent,
    // not the general "no geodata record" the other two actions report.
    case 'courseAdmin.courses.analyze.not_found':
      return 'golfCourseAdminPage.analyzeNoPosition';
    case 'courseAdmin.courses.analyze.unavailable':
      return 'golfCourseAdminPage.analysisNotConfigured';
    default:
      return 'courseAnalysis.status.runFailed';
  }
}

function flyoverFailureKey(code: string | undefined): string {
  switch (code) {
    case 'courseAdmin.flyovers.render.not_found':
      return 'golfCourseAdminPage.actionNoGeodata';
    case 'courseAdmin.flyovers.render.no_holes':
      return 'golfCourseAdminPage.flyoverNoHoles';
    default:
      return 'golfCourseAdminPage.flyoverRenderFailed';
  }
}

/**
 * The course's location on Esri World Imagery — the same satellite source the analyser reads
 * — so an admin can eyeball whether the catalogue coordinate is actually on the course before
 * spending an analysis on it. Zoom 16 frames a whole 18-hole course.
 *
 * ArcGIS's Map Viewer takes `center` as lon,lat, `level` (only honoured alongside `center`),
 * and the basemap as `basemapUrl` = a full map-service URL. A `basemap=satellite` shorthand is
 * silently ignored and you get the default topographic map — which is what shipped first.
 */
const ESRI_WORLD_IMAGERY = 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer';

/** The course's own OSM object when its identity is known, else the map at its position. */
function osmUrl(course: { osmType: string | null; osmId: number | null; lat?: number | null; lon?: number | null }): string {
  if (course.osmType && course.osmId) {
    return `https://www.openstreetmap.org/${course.osmType}/${course.osmId}`;
  }
  return `https://www.openstreetmap.org/#map=17/${(course.lat as number).toFixed(5)}/${(course.lon as number).toFixed(5)}`;
}

function esriImageryUrl(lat: number, lon: number): string {
  return `https://www.arcgis.com/apps/mapviewer/index.html?center=${lon.toFixed(6)},${lat.toFixed(6)}&level=16&basemapUrl=${encodeURIComponent(ESRI_WORLD_IMAGERY)}`;
}

/**
 * Admin-only tool to bulk-import golf courses from OpenStreetMap for a whole country (see
 * AdminMenu in app/layout/RootLayoutNav.tsx). Imported courses land as CourseVisibility.Disabled,
 * pending manual review — this screen's result list is the only record of what a run imported.
 */
export default function GolfCourseAdminScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const router = useRouter();
  const { q, country, status, osm, page, holeMap } = useLocalSearchParams<{
    q?: string;
    country?: string;
    status?: string;
    osm?: string;
    page?: string;
    /** A course id to open the hole map on straight away — the models page links here to look at an evaluation. */
    holeMap?: string;
  }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const requestIdRef = useRef<string | null>(null);
  /** Carried from the `found` progress line to the ack, which does not repeat it. */
  const unnamedCountRef = useRef(0);
  const coursesRequestIdRef = useRef<string | null>(null);
  const backfillRequestIdRef = useRef<string | null>(null);
  const visibilityRequestIdRef = useRef<string | null>(null);
  const featureImportRequestIdRef = useRef<string | null>(null);

  // Search, country, status and page live in the URL, so a reload or a link keeps the exact
  // list an admin was looking at. The URL is the single source of truth — nothing mirrors it
  // into component state.
  const filters = useMemo(
    () => parseCourseListParams({ q, country, status, osm, page }),
    [q, country, status, osm, page],
  );
  const updateFilters = (next: Partial<CourseListFilters>) => {
    router.setParams(courseListParams({ ...filters, ...next }));
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [listCountryPickerOpen, setListCountryPickerOpen] = useState(false);
  const [countryCode, setCountryCode] = useState(DEFAULT_COURSE_ADMIN_COUNTRY_CODE);
  const [importState, setImportState] = useState<ImportState>({ status: 'idle' });
  const importOptionsRequestIdRef = useRef<string | null>(null);
  const [importOptions, setImportOptions] = useState<ImportOptionsState>({ status: 'idle' });
  /** Keys of the sources the admin left checked; starts as all of them when options load. */
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [featureImportState, setFeatureImportState] = useState<FeatureImportState>({ status: 'idle' });
  const [clubEnrichment, setClubEnrichment] = useState<Record<string, ClubEnrichmentState>>({});
  // Which club each in-flight request belongs to, so a late ack lands on the right card.
  const clubEnrichmentRequestsRef = useRef<Record<string, string>>({});
  const extractsRequestIdRef = useRef<string | null>(null);
  const [extracts, setExtracts] = useState<ExtractsState>({ jobs: [], workerOnline: false });
  const extractCoursesRequestIdRef = useRef<string | null>(null);
  const [extractCourses, setExtractCourses] = useState<ExtractCoursesState>({ status: 'idle' });
  /** Outcome groups the admin has expanded to their course names. */
  const [openOutcomes, setOpenOutcomes] = useState<string[]>([]);
  const [coursesState, setCoursesState] = useState<CoursesState>({ status: 'idle' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [analyzeRowState, setAnalyzeRowState] = useState<Record<string, AnalyzeRowState>>({});
  const [backfillState, setBackfillState] = useState<BackfillState>({ status: 'idle' });
  const [visibilityPickerOpen, setVisibilityPickerOpen] = useState(false);
  const [visibilityState, setVisibilityState] = useState<VisibilityActionState>({ status: 'idle' });
  /** Course id of an in-flight per-card visibility change; that card's chips disable while set. */
  const [cardVisibilityBusy, setCardVisibilityBusy] = useState<string | null>(null);
  const [cardVisibilityError, setCardVisibilityError] = useState<string | null>(null);
  /** Read in the socket listener (subscribed once), so a ref rather than the state above. */
  const cardVisibilityCourseRef = useRef<string | null>(null);
  /** The course whose hole map is open, or null. The map is the only per-course overlay left. */
  const [holeMapCourse, setHoleMapCourse] = useState<CourseRow | null>(null);
  /**
   * Opens the hole map on the course the URL names, once. The models page links here from a
   * model's holdout course to look at its evaluation drawn on the holes. Once, so closing the
   * window does not reopen it; and by id even when the course is not on the loaded page —
   * the modal fetches by id and only needs the hole count, for which eighteen is the honest
   * default when the list cannot say.
   */
  const openedFromUrl = useRef(false);
  useEffect(() => {
    if (!holeMap || openedFromUrl.current || coursesState.status !== 'loaded') return;
    openedFromUrl.current = true;
    const listed = coursesState.courses.find((course) => course.id === holeMap);
    setHoleMapCourse(listed ?? ({ id: holeMap, holesCount: 18 } as unknown as CourseRow));
  }, [holeMap, coursesState]);
  const [gisImportState, setGisImportState] = useState<Record<string, GisImportRowState>>({});
  const [flyoverCommandCourse, setFlyoverCommandCourse] = useState<CourseRow | null>(null);
  const [flyoverCommandCopied, setFlyoverCommandCopied] = useState(false);
  const [flyoverStatus, setFlyoverStatus] = useState<Record<string, FlyoverStatus | null>>({});
  const [flyoverRequest, setFlyoverRequest] = useState<Record<string, { state: FlyoverRequestState; code?: string }>>({});
  const [flyoverShowCommand, setFlyoverShowCommand] = useState(false);
  const [osmRefreshState, setOsmRefreshState] = useState<Record<string, OsmRefreshRowState>>({});

  /**
   * Re-reads the catalogue.
   *
   * `keepRows` leaves the rows on screen while the answer comes back, for a refresh a row asked
   * for itself: blanking the list to "Loading courses…" would take the row — and the result line
   * the admin pressed the button to read — off the screen with it.
   */
  const fetchCourses = (options?: { keepRows?: boolean }) => {
    const requestId = createRequestId();
    coursesRequestIdRef.current = requestId;
    if (!options?.keepRows) setCoursesState({ status: 'loading' });
    socketRef.current.send({ type: 'courseAdmin.courses.list.request', requestId });
  };

  /**
   * Queues satellite analysis for one course, sending the position this row already shows.
   * geodata uses it only when it has no record of the course, to create one — otherwise a
   * course that has never been analysed answers "no geodata record yet" while the line above
   * the button displays the coordinate that would have settled it.
   */
  const startAnalyze = (course: CourseRow) => {
    const courseId = course.id;
    setAnalyzeRowState((prev) => ({ ...prev, [courseId]: { status: 'running' } }));
    socketRef.current.send({
      type: 'courseAdmin.courses.analyze.request',
      requestId: createRequestId(),
      courseId,
      name: course.name,
      lat: course.lat,
      lon: course.lon,
    });
  };

  /**
   * Re-reads the course's OpenStreetMap data. Plain HTTP rather than the socket: the answer is
   * the delta (written / updated / removed / unchanged), and it comes back in one round trip once
   * Overpass has answered — nothing to stream.
   */
  const refreshOsm = async (course: CourseRow) => {
    const courseId = course.id;
    if (!user?.token) return;
    setOsmRefreshState((prev) => ({ ...prev, [courseId]: { status: 'running' } }));
    try {
      // Send the OSM identity this row already shows (from the catalogue). geodata uses it only
      // when its own row has none — a course whose geodata row was created by the analysis
      // path, say — instead of answering "import it from OpenStreetMap first" while the row
      // right above the button says "OSM relation/N".
      const identity =
        course.osmType && course.osmId
          ? { osmType: course.osmType, osmId: course.osmId, name: course.name }
          : null;
      const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}/osm/refresh`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user.token}`,
          ...(identity ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(identity ? { body: JSON.stringify(identity) } : {}),
      });
      if (!response.ok) {
        setOsmRefreshState((prev) => ({
          ...prev,
          [courseId]: { status: 'failed', httpStatus: response.status },
        }));
        return;
      }
      const body = (await response.json()) as {
        written?: number;
        updated?: number;
        removed?: number;
        unchanged?: number;
        skipped?: number;
      };
      setOsmRefreshState((prev) => ({
        ...prev,
        [courseId]: {
          status: 'done',
          written: body.written ?? 0,
          updated: body.updated ?? 0,
          removed: body.removed ?? 0,
          unchanged: body.unchanged ?? 0,
          skipped: body.skipped ?? 0,
        },
      }));
      // The OSM feature counts on the card come from the list; re-fetch so they reflect it.
      fetchCourses();
    } catch {
      // No answer at all — nothing to name beyond "it did not get through".
      setOsmRefreshState((prev) => ({ ...prev, [courseId]: { status: 'failed', httpStatus: null } }));
    }
  };

  /**
   * Imports this course's scorecard from Golf i Sverige, now.
   *
   * Forced and attended server-side, because the automatic path skips a course it has already
   * fetched and paces itself against a daily budget — neither of which is what pressing the
   * button means. It fetches the club's published card, so it is slow; the row says so while
   * it runs and reports what was written when it lands.
   */
  const importGis = async (course: CourseRow) => {
    const courseId = course.id;
    if (!user?.token) return;
    setGisImportState((prev) => ({ ...prev, [courseId]: { status: 'running' } }));
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/geodata/courses/${encodeURIComponent(courseId)}/scorecard/import`,
        { method: 'POST', headers: { Authorization: `Bearer ${user.token}` } },
      );
      if (!response.ok) {
        setGisImportState((prev) => ({
          ...prev,
          [courseId]: { status: 'failed', httpStatus: response.status },
        }));
        return;
      }
      const body = (await response.json()) as {
        clubSlug?: string | null;
        holesWritten?: number;
        teesWritten?: number;
        reason?: string | null;
        outcome?: string | null;
      };
      setGisImportState((prev) => ({
        ...prev,
        [courseId]: {
          status: 'done',
          holes: body.holesWritten ?? 0,
          tees: body.teesWritten ?? 0,
          clubSlug: body.clubSlug ?? null,
          // The service's own note first — it names what happened in words ("every row on the
          // page belongs to a person who entered it") where the outcome is a single word.
          reason: body.reason ?? body.outcome ?? null,
        },
      }));
      // The card's "Golf i Sverige: … holes · … tees" line comes from the list; re-read it so
      // it reflects what just landed rather than what was there before the button was pressed.
      fetchCourses({ keepRows: true });
    } catch {
      setGisImportState((prev) => ({ ...prev, [courseId]: { status: 'failed', httpStatus: null } }));
    }
  };

  const toggleSelected = (courseId: string) => {
    setSelectedIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId],
    );
  };

  /**
   * The scripts/render_flyovers.py invocation for one course, ready to paste into a
   * terminal at the repo root. Flyover videos are rendered on a laptop by design (a
   * course is a render-once job, not a pipeline), so the admin surface's contribution
   * is handing over the exact command. Every value is the production one — the STAC
   * catalogues from the course-analysis Pulumi config, the imagery bucket, the
   * Geotorget credential path, this environment's own Keycloak realm — so the command
   * pastes straight into a shell and runs. It carries no credential: the script signs
   * the user in via the OAuth device flow, approved in a browser that is already
   * logged in (Google-federated logins included).
   */
  const flyoverCommandFor = (courseId: string) =>
    [
      'PYTHONPATH=services/course-analysis-service/src \\',
      '  IMAGERY_STAC_URL=https://api.lantmateriet.se/stac-bild/v1 \\',
      '  ELEVATION_STAC_URL=https://api.lantmateriet.se/stac-hojd/v1 \\',
      '  SECRETS_NAME=goffy-Production SECRETS_REGION=eu-west-1 \\',
      '  LANTMATERIET_IMAGE_BUCKET=goffy-course-imagery \\',
      '  LANTMATERIET_IMAGE_REGION=eu-west-1 \\',
      `  GOFFY_AUTH_URL=${KEYCLOAK_BASE} \\`,
      '  python3 scripts/render_flyovers.py \\',
      `    --api-base-url ${apiBaseUrl} \\`,
      `    --course-id ${courseId} \\`,
      '    --output-dir /tmp/flyovers',
    ].join('\n');

  const copyFlyoverCommand = (courseId: string) => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.clipboard) {
      return;
    }
    void navigator.clipboard
      .writeText(flyoverCommandFor(courseId))
      .then(() => setFlyoverCommandCopied(true))
      .catch(() => {});
  };

  const requestFlyoverStatus = (courseId: string) => {
    socketRef.current.send({ type: 'courseAdmin.flyovers.status.request', requestId: createRequestId(), courseId });
  };

  const openFlyoverModal = (course: CourseRow) => {
    setFlyoverCommandCopied(false);
    setFlyoverShowCommand(false);
    // Last visit's outcome, queued or failed, said something about a request that is over. The
    // status request below is what this visit knows.
    setFlyoverRequest((prev) => ({ ...prev, [course.id]: { state: 'idle' } }));
    setFlyoverCommandCourse(course);
    requestFlyoverStatus(course.id);
  };

  /** Queue the render on a batch worker (services/batch-worker); the ack only says "queued". */
  const startFlyoverRender = (courseId: string) => {
    setFlyoverRequest((prev) => ({ ...prev, [courseId]: { state: 'requesting' } }));
    socketRef.current.send({ type: 'courseAdmin.flyovers.render.request', requestId: createRequestId(), courseId });
  };

  /** "Refresh OSM" for the selected courses: scoped to selectedIds, not the whole catalogue. */
  const startBackfill = () => {
    const requestId = createRequestId();
    backfillRequestIdRef.current = requestId;
    setBackfillState({ status: 'running', matched: 0, triggeredWithoutMatch: 0, failed: 0 });
    socketRef.current.send({ type: 'courseAdmin.analysis.backfill.request', requestId, courseIds: selectedIds });
  };

  /**
   * Sets one visibility on every selected course. This is the only way to reach a course that is
   * already disabled — the course editor only lets an admin manage official and pending ones, so
   * an OSM-imported course (which lands disabled) could otherwise never be published or made
   * private by anyone but its creator.
   */
  /** One card's chips. Same message and ack path as the bulk action, for a single course. */
  const setVisibilityForCourse = (courseId: string, visibility: SettableVisibility) => {
    const requestId = createRequestId();
    visibilityRequestIdRef.current = requestId;
    cardVisibilityCourseRef.current = courseId;
    setCardVisibilityBusy(courseId);
    setCardVisibilityError(null);
    socketRef.current.send({
      type: 'courseAdmin.courses.visibility.request',
      requestId,
      courseIds: [courseId],
      visibility,
    });
  };

  const setVisibilityForSelected = (visibility: SettableVisibility) => {
    const requestId = createRequestId();
    visibilityRequestIdRef.current = requestId;
    cardVisibilityCourseRef.current = null;
    setVisibilityPickerOpen(false);
    setVisibilityState({ status: 'running' });
    socketRef.current.send({
      type: 'courseAdmin.courses.visibility.request',
      requestId,
      courseIds: selectedIds,
      visibility,
    });
  };

  useEffect(() => {
    if (!user?.token) {
      return;
    }
    socketRef.current.setAuthToken(user.token);
  }, [user?.token]);

  useEffect(() => {
    if (!isAdmin || !user?.token) {
      return;
    }
    fetchCourses();
  }, [isAdmin, user?.token]);

  useEffect(() => {
    const unsubscribe = socketRef.current.onMessage((message) => {
      const payload = message as { type?: string; requestId?: string };

      if (payload.type?.startsWith('courseAdmin.courses.list.')) {
        if (payload.requestId !== coursesRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.courses.list.ack') {
          const ack = message as { totalCount?: number; courses?: CourseRow[] };
          setCoursesState({
            status: 'loaded',
            totalCount: ack.totalCount ?? 0,
            // A row from a BFF that predates the OSM-feature lookup carries no counts; treat that
            // as "nothing imported" so the filters and the row line have one shape to handle.
            courses: (ack.courses ?? []).map((course) => ({
              ...course,
              osmFeatures: course.osmFeatures ?? NO_OSM_FEATURES,
            })),
          });
        } else if (payload.type === 'courseAdmin.courses.list.error') {
          setCoursesState({ status: 'error' });
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.courses.visibility.')) {
        if (payload.requestId !== visibilityRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.courses.visibility.ack') {
          const ack = message as { visibility?: string; updatedCourseIds?: string[]; failedCount?: number };
          const changed = new Set(ack.updatedCourseIds ?? []);
          const visibility = ack.visibility ?? '';
          // Patch the rows that changed instead of refetching: the list request costs four bulk
          // lookups across two services, and this is the one field it would come back different in.
          setCoursesState((prev) =>
            prev.status === 'loaded'
              ? {
                  ...prev,
                  courses: prev.courses.map((course) =>
                    changed.has(course.id) ? { ...course, visibility } : course,
                  ),
                }
              : prev,
          );
          if (cardVisibilityCourseRef.current) {
            if (changed.size === 0) {
              setCardVisibilityError(cardVisibilityCourseRef.current);
            }
            cardVisibilityCourseRef.current = null;
            setCardVisibilityBusy(null);
          } else {
            setVisibilityState({ status: 'done', updated: changed.size, failed: ack.failedCount ?? 0 });
          }
        } else if (payload.type === 'courseAdmin.courses.visibility.error') {
          if (cardVisibilityCourseRef.current) {
            setCardVisibilityError(cardVisibilityCourseRef.current);
            cardVisibilityCourseRef.current = null;
            setCardVisibilityBusy(null);
          } else {
            setVisibilityState({ status: 'error' });
          }
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.analysis.backfill.')) {
        if (payload.requestId !== backfillRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.analysis.backfill.progress') {
          const progress = message as { outcome?: string };
          setBackfillState((prev) => {
            if (prev.status !== 'running') {
              return prev;
            }
            return {
              status: 'running',
              matched: prev.matched + (progress.outcome === 'matched' ? 1 : 0),
              triggeredWithoutMatch: prev.triggeredWithoutMatch + (progress.outcome === 'triggeredWithoutMatch' ? 1 : 0),
              failed: prev.failed + (progress.outcome === 'failed' ? 1 : 0),
            };
          });
        } else if (payload.type === 'courseAdmin.analysis.backfill.ack') {
          const ack = message as {
            matchedCount?: number;
            triggeredWithoutMatchCount?: number;
            failedCount?: number;
          };
          setBackfillState({
            status: 'done',
            matched: ack.matchedCount ?? 0,
            triggeredWithoutMatch: ack.triggeredWithoutMatchCount ?? 0,
            failed: ack.failedCount ?? 0,
          });
          fetchCourses();
        } else if (payload.type === 'courseAdmin.analysis.backfill.error') {
          setBackfillState({ status: 'error' });
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.flyovers.')) {
        const flyoverPayload = message as {
          courseId?: string;
          code?: string;
          status?: FlyoverStatus;
        };
        const courseId = flyoverPayload.courseId;
        if (!courseId) {
          return;
        }
        if (payload.type === 'courseAdmin.flyovers.render.ack') {
          setFlyoverRequest((prev) => ({ ...prev, [courseId]: { state: 'queued' } }));
          requestFlyoverStatus(courseId);
        } else if (payload.type === 'courseAdmin.flyovers.render.error') {
          setFlyoverRequest((prev) => ({ ...prev, [courseId]: { state: 'failed', code: flyoverPayload.code } }));
        } else if (payload.type === 'courseAdmin.flyovers.status.response') {
          setFlyoverStatus((prev) => ({ ...prev, [courseId]: flyoverPayload.status ?? null }));
        } else if (payload.type === 'courseAdmin.flyovers.status.error') {
          // Drop what the last successful status said rather than leaving it on screen: a stale
          // "a batch worker is online" outlives the fact it reported and reads as current.
          setFlyoverStatus((prev) => ({ ...prev, [courseId]: null }));
        }
        return;
      }

      // Keyed by courseId rather than requestId: several rows can each have a trigger
      // in flight at once, and the response says which one it answers.
      if (payload.type?.startsWith('courseAdmin.courses.analyze.')) {
        const analyzePayload = message as { courseId?: string; code?: string };
        if (!analyzePayload.courseId) {
          return;
        }
        if (payload.type === 'courseAdmin.courses.analyze.ack') {
          setAnalyzeRowState((prev) => ({ ...prev, [analyzePayload.courseId!]: { status: 'queued' } }));
        } else if (payload.type === 'courseAdmin.courses.analyze.error') {
          setAnalyzeRowState((prev) => ({
            ...prev,
            [analyzePayload.courseId!]: { status: 'failed', code: analyzePayload.code },
          }));
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.osm.extracts.')) {
        if (payload.requestId !== extractsRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.osm.extracts.response') {
          const response = message as { jobs?: ExtractJob[]; workerOnline?: boolean };
          const jobs = response.jobs ?? [];
          setExtracts({ jobs, workerOnline: response.workerOnline ?? false });
          // A finished run has written this country's rows; the list behind the modal is stale.
          if (jobs.some((job) => job.status === 'succeeded')) {
            fetchCourses();
          }
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.osm.extractCourses.')) {
        if (payload.requestId !== extractCoursesRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.osm.extractCourses.response') {
          const response = message as { jobId?: string; courses?: ExtractCourseOutcome[] };
          setExtractCourses({ status: 'loaded', jobId: response.jobId ?? '', courses: response.courses ?? [] });
        } else if (payload.type === 'courseAdmin.osm.extractCourses.error') {
          setExtractCourses((prev) => ({ status: 'error', jobId: prev.status === 'idle' ? '' : prev.jobId }));
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.osm.import.options.')) {
        if (payload.requestId !== importOptionsRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.osm.import.options.ack') {
          const ack = message as { sources?: ImportSourceOption[] };
          const sources = ack.sources ?? [];
          setImportOptions({ status: 'loaded', sources });
          setSelectedSources(sources.map((source) => source.key));
        } else if (payload.type === 'courseAdmin.osm.import.options.error') {
          setImportOptions({ status: 'error' });
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.clubEnrichment.')) {
        // A frame from a run this screen did not start, or from one whose card is long gone.
        const clubId = payload.requestId ? clubEnrichmentRequestsRef.current[payload.requestId] : undefined;
        if (!clubId) {
          return;
        }
        delete clubEnrichmentRequestsRef.current[payload.requestId as string];

        if (payload.type === 'courseAdmin.clubEnrichment.ack') {
          const ack = message as {
            clubs?: Array<{ enriched?: boolean; gotLogo?: boolean; gotDescription?: boolean; skippedReason?: string | null }>;
          };
          // One club was asked for, so one club comes back. An empty list means the service had
          // nothing to say about it, which reads the same as a skip with no reason given.
          const only = (ack.clubs ?? [])[0];
          setClubEnrichment((current) => ({
            ...current,
            [clubId]: {
              status: 'done',
              enriched: only?.enriched ?? false,
              gotLogo: only?.gotLogo ?? false,
              gotDescription: only?.gotDescription ?? false,
              skippedReason: only?.skippedReason ?? null,
            },
          }));
          // The club now carries a website, a logo and a description the list shows, so re-read
          // it — keeping the rows on screen, because clearing them would take the card, and the
          // message the admin pressed the button to read, off the screen with it.
          fetchCourses({ keepRows: true });
        } else if (payload.type === 'courseAdmin.clubEnrichment.error') {
          const error = message as { code?: string };
          setClubEnrichment((current) => ({
            ...current,
            [clubId]: { status: 'error', code: error.code ?? null },
          }));
        }
        return;
      }

      if (payload.type?.startsWith('courseAdmin.osm.featureImport.')) {
        if (payload.requestId !== featureImportRequestIdRef.current) {
          return;
        }
        if (payload.type === 'courseAdmin.osm.featureImport.ack') {
          // The country went to the batch fleet; the run outlives this screen, so from here
          // its state is read from the extract list rather than streamed to us.
          const ack = message as { coursesRequested?: number };
          setFeatureImportState({ status: 'queued', coursesRequested: ack.coursesRequested ?? 0 });
          fetchExtracts();
        } else if (payload.type === 'courseAdmin.osm.featureImport.error') {
          const error = message as { code?: string };
          setFeatureImportState({ status: 'error', code: error.code ?? null });
        }
        return;
      }

      if (payload.requestId !== requestIdRef.current) {
        return;
      }
      if (payload.type === 'courseAdmin.osm.import.progress') {
        const progress = message as {
          phase?: string;
          totalFoundInCountry?: number;
          unnamedInCountry?: number;
          source?: string;
          total?: number;
        };
        if (progress.phase === 'authoritativeSource' && typeof progress.source === 'string') {
          setImportState({
            status: 'importing',
            progress: { phase: 'authoritativeSource', source: progress.source },
          });
        } else if (progress.phase === 'searching') {
          setImportState({ status: 'importing', progress: { phase: 'searching' } });
        } else if (progress.phase === 'found' && typeof progress.totalFoundInCountry === 'number') {
          const unnamed = progress.unnamedInCountry ?? 0;
          unnamedCountRef.current = unnamed;
          setImportState({
            status: 'importing',
            progress: {
              phase: 'found',
              totalFoundInCountry: progress.totalFoundInCountry,
              unnamedInCountry: unnamed,
            },
          });
        } else if (progress.phase === 'importing' && typeof progress.total === 'number') {
          setImportState({ status: 'importing', progress: { phase: 'importing', total: progress.total } });
        }
      } else if (payload.type === 'courseAdmin.osm.import.ack') {
        const ack = message as {
          importedCount?: number;
          skippedCount?: number;
          failedCount?: number;
          imported?: ImportedCourse[];
          authoritativeSource?: AuthoritativeSourceResult | null;
        };
        setImportState({
          status: 'done',
          importedCount: ack.importedCount ?? 0,
          skippedCount: ack.skippedCount ?? 0,
          failedCount: ack.failedCount ?? 0,
          // Only the `found` progress line carries this; the ack counts what golf-courses-service
          // was asked to import, which the nameless ones never reach.
          unnamedCount: unnamedCountRef.current,
          imported: ack.imported ?? [],
          authoritativeSource: ack.authoritativeSource ?? null,
        });
        // The authoritative source writes boundaries even when OSM adds no courses, and those
        // are what the list shows as geodata coverage — so refresh on either.
        if ((ack.importedCount ?? 0) > 0 || ack.authoritativeSource?.result) {
          fetchCourses();
        }
      } else if (payload.type === 'courseAdmin.osm.import.error') {
        setImportState({ status: 'error' });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const selectedCountry =
    COURSE_ADMIN_COUNTRIES.find((c) => c.code === countryCode) ?? COURSE_ADMIN_COUNTRIES[0];

  /** How the national extract imports are going. Cheap, so it is also the queued run's progress. */
  const fetchExtracts = () => {
    const requestId = createRequestId();
    extractsRequestIdRef.current = requestId;
    socketRef.current.send({ type: 'courseAdmin.osm.extracts.request', requestId });
  };

  /** One run's courses and what became of each, opened on demand and closed the same way. */
  const toggleExtractCourses = (jobId: string) => {
    if (extractCourses.status !== 'idle' && extractCourses.jobId === jobId) {
      setExtractCourses({ status: 'idle' });
      return;
    }
    const requestId = createRequestId();
    extractCoursesRequestIdRef.current = requestId;
    setExtractCourses({ status: 'loading', jobId });
    setOpenOutcomes([]);
    socketRef.current.send({ type: 'courseAdmin.osm.extractCourses.request', requestId, jobId });
  };

  const extractOutcomeLabel = (outcome: string) =>
    t(`golfCourseAdminPage.${EXTRACT_OUTCOME_KEYS[outcome] ?? 'extractOutcomeOther'}`, { outcome });

  /** What this country's import would run, with per-source already-imported counts. */
  const fetchImportOptions = (code: string) => {
    const requestId = createRequestId();
    importOptionsRequestIdRef.current = requestId;
    setImportOptions({ status: 'loading' });
    const country = COURSE_ADMIN_COUNTRIES.find((c) => c.code === code);
    socketRef.current.send({
      type: 'courseAdmin.osm.import.options.request',
      requestId,
      countryCode: code,
      countryName: country?.name,
    });
  };

  const openModal = () => {
    setImportState({ status: 'idle' });
    setModalOpen(true);
    fetchImportOptions(countryCode);
    // A run queued on an earlier visit may still be going; show it rather than an idle button.
    fetchExtracts();
  };

  const closeModal = () => {
    setModalOpen(false);
    setCountryPickerOpen(false);
  };

  const onImport = () => {
    const requestId = createRequestId();
    requestIdRef.current = requestId;
    unnamedCountRef.current = 0;
    setImportState({ status: 'importing', progress: null });
    socketRef.current.send({
      type: 'courseAdmin.osm.import.request',
      requestId,
      countryCode,
      // Only when the checkboxes rendered: an options lookup that failed sends no list, which
      // the BFF reads as "run everything" — the behaviour from before sources were selectable.
      sources: importOptions.status === 'loaded' ? selectedSources : undefined,
    });
  };

  /** Scoped to the modal's country picker, like the course import above it. Which courses the
   * country holds is geodata's to read from the catalogue; the name rides along because the
   * catalogue stores either spelling of a club's country ("Sweden" from OSM imports, "SE" from
   * the club API). */
  const onImportFeatures = () => {
    const requestId = createRequestId();
    featureImportRequestIdRef.current = requestId;
    setFeatureImportState({ status: 'running' });
    socketRef.current.send({
      type: 'courseAdmin.osm.featureImport.request',
      requestId,
      countryCode: selectedCountry.code,
      countryName: selectedCountry.name,
    });
  };

  /**
   * Collect one club's details, from the card of a course it owns.
   *
   * No `force`: that flag only reaches `PickAsync`, which chooses clubs for a country run and
   * skips any looked at inside the refresh window. Naming one club bypasses that choosing
   * entirely, so the run always happens and sending the flag would suggest otherwise.
   */
  const onFetchClubDetails = (clubId: string) => {
    const requestId = createRequestId();
    clubEnrichmentRequestsRef.current[requestId] = clubId;
    setClubEnrichment((current) => ({ ...current, [clubId]: { status: 'running' } }));
    socketRef.current.send({
      type: 'courseAdmin.clubEnrichment.request',
      requestId,
      clubId,
    });
  };

  const latestExtract = extracts.jobs[0] ?? null;
  const extractRunning = latestExtract?.status === 'queued';

  // A queued extract runs on somebody else's machine, so nothing pushes its progress here: ask
  // while one is outstanding and the modal is open, and stop as soon as it is not.
  useEffect(() => {
    if (!modalOpen || !extractRunning) {
      return;
    }

    const timer = setInterval(fetchExtracts, 10000);
    return () => clearInterval(timer);
  }, [modalOpen, extractRunning]);

  // Browsing, not just searching: entering the screen shows the courses of the selected country
  // straight away, a page at a time, rather than an empty list until someone types a name.
  const allCourses = coursesState.status === 'loaded' ? coursesState.courses : EMPTY_COURSES;
  const filteredCourses = useMemo(() => filterCourses(allCourses, filters), [allCourses, filters]);
  const currentPage = useMemo(() => pageOf(filteredCourses, filters.page), [filteredCourses, filters.page]);
  const searchResults = currentPage.items;

  const selectedListCountry =
    filters.countryCode === ALL_COUNTRIES
      ? null
      : (COURSE_ADMIN_COUNTRIES.find((c) => c.code === filters.countryCode) ?? null);

  // "All shown" means the page in front of the admin, not the whole filtered set — the two are
  // the same only until a filter matches more than one page.
  const allShownSelected =
    searchResults.length > 0 && searchResults.every((course) => selectedIds.includes(course.id));
  const toggleAllShown = () => {
    const shownIds = searchResults.map((course) => course.id);
    setSelectedIds((prev) =>
      allShownSelected
        ? prev.filter((id) => !shownIds.includes(id))
        : [...prev, ...shownIds.filter((id) => !prev.includes(id))],
    );
  };
  const analyzeSelected = () => {
    // By course rather than by id: the request carries the row's position, and a selected id
    // whose row is no longer loaded has none to send.
    allCourses.filter((course) => selectedIds.includes(course.id)).forEach(startAnalyze);
  };
  // One request per course, same as analyzeSelected: the worker takes one job at a time and the
  // queue is durable, so fanning out here is no worse than a batch message would be.
  const flyoverSelected = () => {
    selectedIds.forEach(startFlyoverRender);
  };

  return (
    <>
      <Stack.Screen options={{ title: t('golfCourseAdminPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('golfCourseAdminPage.title')}</Text>

        {!isAdmin ? (
          <Text style={styles.paragraph}>{t('golfCourseAdminPage.forbidden')}</Text>
        ) : (
          <>
            {coursesState.status === 'loaded' ? (
              <Text testID="golf-course-admin-total-count" style={styles.paragraph}>
                {t('golfCourseAdminPage.totalCourses', { count: String(coursesState.totalCount) })}
              </Text>
            ) : coursesState.status === 'loading' ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={appColors.text.primary} />
                <Text style={styles.paragraph}>{t('golfCourseAdminPage.loadingCourses')}</Text>
              </View>
            ) : coursesState.status === 'error' ? (
              <Text style={styles.errorText}>{t('golfCourseAdminPage.loadCoursesError')}</Text>
            ) : null}

            <Pressable
              testID="golf-course-admin-find-osm"
              accessibilityRole="button"
              style={styles.scanButton}
              onPress={openModal}
            >
              <Ionicons name="search" size={18} color={appColors.text.primary} />
              <Text style={styles.scanButtonText}>{t('golfCourseAdminPage.findOsmButton')}</Text>
            </Pressable>

            <View style={styles.searchRow}>
              <Ionicons name="search" size={16} color={appColors.text.secondary} />
              <TextInput
                testID="golf-course-admin-search-input"
                style={styles.searchInput}
                value={filters.query}
                onChangeText={(text) => updateFilters({ query: text, page: 1 })}
                placeholder={t('golfCourseAdminPage.searchPlaceholder')}
                placeholderTextColor={appColors.text.secondary}
                autoCorrect={false}
                autoCapitalize="none"
              />
              {filters.query.length > 0 ? (
                <Pressable
                  testID="golf-course-admin-search-clear"
                  accessibilityRole="button"
                  onPress={() => updateFilters({ query: '', page: 1 })}
                  hitSlop={8}
                >
                  <Ionicons name="close-circle" size={16} color={appColors.text.secondary} />
                </Pressable>
              ) : null}
            </View>

            <Pressable
              testID="golf-course-admin-list-country"
              accessibilityRole="button"
              style={styles.listCountryRow}
              onPress={() => setListCountryPickerOpen(true)}
            >
              <Ionicons name="flag-outline" size={16} color={appColors.text.secondary} />
              <Text style={styles.officialLabel}>{t('golfCourseAdminPage.countryLabel')}</Text>
              <Text testID="golf-course-admin-list-country-value" style={styles.pickerOptionText}>
                {selectedListCountry?.name ?? t('golfCourseAdminPage.countryAll')}
              </Text>
              <Ionicons name="chevron-down" size={16} color={appColors.text.secondary} />
            </Pressable>

            <View style={styles.filterRow}>
              {(['all', 'analysed', 'notAnalysed'] as const).map((filter) => (
                <Pressable
                  key={filter}
                  testID={`golf-course-admin-filter-${filter}`}
                  accessibilityRole="button"
                  accessibilityState={{ selected: filters.analysisFilter === filter }}
                  style={[styles.filterChip, filters.analysisFilter === filter && styles.filterChipActive]}
                  onPress={() => updateFilters({ analysisFilter: filter as AnalysisFilter, page: 1 })}
                >
                  <Text
                    style={[
                      styles.filterChipText,
                      filters.analysisFilter === filter && styles.filterChipTextActive,
                    ]}
                  >
                    {t(`golfCourseAdminPage.filter${filter[0].toUpperCase()}${filter.slice(1)}`)}
                  </Text>
                </Pressable>
              ))}
            </View>

            {/* What the OSM import actually stored, not what OSM holds — a course can be matched
                to OSM and still have none of these. */}
            <View style={styles.filterRow}>
              {OSM_FEATURE_KINDS.map((kind) => {
                const active = filters.osmFeatures.includes(kind);
                return (
                  <Pressable
                    key={kind}
                    testID={`golf-course-admin-osm-filter-${kind}`}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: active }}
                    style={styles.osmFilterChip}
                    onPress={() =>
                      updateFilters({
                        osmFeatures: active
                          ? filters.osmFeatures.filter((existing) => existing !== kind)
                          : [...filters.osmFeatures, kind],
                        page: 1,
                      })
                    }
                  >
                    <View style={[styles.smallCheckbox, active && styles.checkboxChecked]}>
                      {active ? <Text style={styles.smallCheckboxMark}>✓</Text> : null}
                    </View>
                    <Text style={[styles.filterChipText, active && styles.filterChipTextActive]}>
                      {t(`golfCourseAdminPage.osmFilter${kind[0].toUpperCase()}${kind.slice(1)}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            {searchResults.length > 0 ? (
              <View style={styles.selectionRow}>
                <Pressable
                  testID="golf-course-admin-select-all"
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: allShownSelected }}
                  style={styles.selectAllRow}
                  onPress={toggleAllShown}
                >
                  <View style={[styles.checkbox, allShownSelected && styles.checkboxChecked]}>
                    {allShownSelected ? <Text style={styles.checkboxMark}>✓</Text> : null}
                  </View>
                  <Text style={styles.paragraph}>{t('golfCourseAdminPage.selectAllShown')}</Text>
                </Pressable>
                {selectedIds.length > 0 ? (
                  <>
                    <Text testID="golf-course-admin-selected-count" style={styles.progressText}>
                      {t('golfCourseAdminPage.selectedCount', { count: String(selectedIds.length) })}
                    </Text>
                    <Pressable
                      testID="golf-course-admin-analyze-selected"
                      accessibilityRole="button"
                      style={styles.analyzeButton}
                      onPress={analyzeSelected}
                    >
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.analyzeSelected')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-flyover-selected"
                      accessibilityRole="button"
                      style={styles.analyzeButton}
                      onPress={flyoverSelected}
                    >
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.flyoverSelected')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-backfill"
                      accessibilityRole="button"
                      disabled={backfillState.status === 'running'}
                      style={[styles.analyzeButton, backfillState.status === 'running' && styles.actionButtonDisabled]}
                      onPress={startBackfill}
                    >
                      {backfillState.status === 'running' ? <ActivityIndicator color={appColors.text.primary} /> : null}
                      <Text style={styles.analyzeButtonText}>
                        {backfillState.status === 'running'
                          ? t('golfCourseAdminPage.backfillRunning')
                          : t('golfCourseAdminPage.backfillButton')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-set-visibility"
                      accessibilityRole="button"
                      disabled={visibilityState.status === 'running'}
                      style={[styles.analyzeButton, visibilityState.status === 'running' && styles.actionButtonDisabled]}
                      onPress={() => setVisibilityPickerOpen(true)}
                    >
                      {visibilityState.status === 'running' ? (
                        <ActivityIndicator color={appColors.text.primary} />
                      ) : null}
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.setVisibility')}
                      </Text>
                    </Pressable>
                    <Pressable
                      testID="golf-course-admin-clear-selection"
                      accessibilityRole="button"
                      style={styles.analyzeButton}
                      onPress={() => setSelectedIds([])}
                    >
                      <Text style={styles.analyzeButtonText}>
                        {t('golfCourseAdminPage.clearSelection')}
                      </Text>
                    </Pressable>
                  </>
                ) : null}
              </View>
            ) : null}

            {backfillState.status === 'running' || backfillState.status === 'done' ? (
              <Text
                testID={
                  backfillState.status === 'running'
                    ? 'golf-course-admin-backfill-progress'
                    : 'golf-course-admin-backfill-result'
                }
                style={styles.progressText}
              >
                {t(
                  backfillState.status === 'running'
                    ? 'golfCourseAdminPage.backfillProgress'
                    : 'golfCourseAdminPage.backfillDone',
                  {
                    matched: String(backfillState.matched),
                    triggered: String(backfillState.triggeredWithoutMatch),
                    failed: String(backfillState.failed),
                  },
                )}
              </Text>
            ) : backfillState.status === 'error' ? (
              <Text testID="golf-course-admin-backfill-error" style={styles.errorText}>
                {t('golfCourseAdminPage.backfillError')}
              </Text>
            ) : null}

            {visibilityState.status === 'done' ? (
              <Text testID="golf-course-admin-visibility-result" style={styles.progressText}>
                {t('golfCourseAdminPage.visibilityDone', {
                  updated: String(visibilityState.updated),
                  failed: String(visibilityState.failed),
                })}
              </Text>
            ) : visibilityState.status === 'error' ? (
              <Text testID="golf-course-admin-visibility-error" style={styles.errorText}>
                {t('golfCourseAdminPage.visibilityError')}
              </Text>
            ) : null}

            {coursesState.status === 'loaded' ? (
              searchResults.length === 0 ? (
                <Text style={styles.paragraph}>{t('golfCourseAdminPage.searchNoMatches')}</Text>
              ) : (
                <View style={styles.searchResults}>
                  {searchResults.map((course) => (
                    // Not pressable itself any more: it used to open a detail popup, and every
                    // action that popup held is now a button on the card. A card that still
                    // reacted to a press would have nothing left to open.
                    <View
                      key={course.id}
                      testID="golf-course-admin-search-result"
                      style={styles.courseCard}
                    >
                      <View style={styles.courseCardHeader}>
                        <Pressable
                          testID="golf-course-admin-select-course"
                          accessibilityRole="checkbox"
                          accessibilityState={{ checked: selectedIds.includes(course.id) }}
                          hitSlop={8}
                          onPress={(event) => {
                            event.stopPropagation();
                            toggleSelected(course.id);
                          }}
                        >
                          <View
                            style={[styles.checkbox, selectedIds.includes(course.id) && styles.checkboxChecked]}
                          >
                            {selectedIds.includes(course.id) ? (
                              <Text style={styles.checkboxMark}>✓</Text>
                            ) : null}
                          </View>
                        </Pressable>
                        <Text style={styles.courseCardName}>{course.name}</Text>
                      </View>
                      <Text style={styles.courseCardMeta}>
                        {[course.clubName, course.city, course.country].filter(Boolean).join(' · ')}
                      </Text>
                      {course.clubWebsiteUrl ? (
                        <Pressable
                          testID={`golf-course-admin-club-website-${course.id}`}
                          onPress={() => void Linking.openURL(course.clubWebsiteUrl as string)}
                        >
                          <Text style={styles.inlineLink} numberOfLines={1}>
                            {course.clubWebsiteUrl}
                          </Text>
                        </Pressable>
                      ) : null}
                      <Text style={styles.courseCardMeta}>
                        {t('golfCourseAdminPage.courseCardDetails', {
                          par: String(course.par),
                          holes: String(course.holesCount),
                          tees: String(course.teeCount ?? 0),
                          visibility: course.visibility,
                        })}
                      </Text>
                      <View style={styles.visibilityChipRow}>
                        {SETTABLE_VISIBILITIES.map((visibility) => {
                          const active = course.visibility === visibility;
                          const busy = cardVisibilityBusy === course.id;
                          return (
                            <Pressable
                              key={visibility}
                              testID={`course-visibility-${visibility}-${course.id}`}
                              accessibilityRole="button"
                              accessibilityState={{ selected: active, disabled: active || busy }}
                              disabled={active || busy}
                              hitSlop={4}
                              style={[
                                styles.visibilityChip,
                                active && styles.visibilityChipActive,
                                busy && styles.actionButtonDisabled,
                              ]}
                              onPress={(event) => {
                                event.stopPropagation();
                                setVisibilityForCourse(course.id, visibility);
                              }}
                            >
                              <Text style={[styles.visibilityChipText, active && styles.visibilityChipTextActive]}>
                                {t(`golfCourseAdminPage.visibilityChip${visibility[0].toUpperCase()}${visibility.slice(1)}`)}
                              </Text>
                            </Pressable>
                          );
                        })}
                        {cardVisibilityBusy === course.id ? (
                          <ActivityIndicator size="small" color={appColors.text.primary} />
                        ) : null}
                        {/* Straight to the course editor, which is where tees, holes and ratings
                            are actually entered. It was reachable only through the detail modal's
                            OSM-import button, so editing a course meant opening it, then pressing
                            something that says "import" and does not sound like editing. */}
                        <Pressable
                          testID={`golf-course-admin-edit-${course.id}`}
                          accessibilityRole="button"
                          accessibilityLabel={t('golfCourseAdminPage.editCourse')}
                          hitSlop={4}
                          style={[styles.visibilityChip, styles.editChip]}
                          onPress={(event) => {
                            event.stopPropagation();
                            router.push(
                              `/config-course?manage=1&courseId=${encodeURIComponent(course.id)}`
                            );
                          }}
                        >
                          <Ionicons name="create-outline" size={14} color={appColors.text.primary} />
                          <Text style={styles.visibilityChipText}>
                            {t('golfCourseAdminPage.editCourse')}
                          </Text>
                        </Pressable>
                        {/* Where to go and look at what "Club info" just collected. The button
                            that produces a club's logo, description and facilities was on this
                            card and the only way to see the result was a link two levels inside a
                            tour's round settings — so the answer to "where can I see it" was a
                            route nobody would find. Same gate as the button: no clubId, no link,
                            rather than one that goes to /club/undefined. */}
                        {course.clubId ? (
                          <Pressable
                            testID={`golf-course-admin-open-club-${course.id}`}
                            accessibilityRole="link"
                            accessibilityLabel={t('golfCourseAdminPage.openClubPage')}
                            hitSlop={4}
                            style={[styles.visibilityChip, styles.editChip]}
                            onPress={(event) => {
                              event.stopPropagation();
                              router.push(`/club/${course.clubId}`);
                            }}
                          >
                            <Ionicons name="business-outline" size={14} color={appColors.text.primary} />
                            <Text style={styles.visibilityChipText}>
                              {t('golfCourseAdminPage.openClubPage')}
                            </Text>
                          </Pressable>
                        ) : null}
                      </View>
                      {cardVisibilityError === course.id ? (
                        <Text style={styles.errorText}>{t('golfCourseAdminPage.visibilityError')}</Text>
                      ) : null}
                      {typeof course.lat === 'number' && typeof course.lon === 'number' ? (
                        <View style={styles.coordinateRow}>
                          <Text style={styles.courseCardMeta}>
                            {t('golfCourseAdminPage.courseCardCoordinates', {
                              lat: course.lat.toFixed(5),
                              lon: course.lon.toFixed(5),
                            })}
                          </Text>
                          <Pressable
                            testID="golf-course-admin-open-in-esri"
                            accessibilityRole="link"
                            hitSlop={8}
                            onPress={(event) => {
                              event.stopPropagation();
                              void Linking.openURL(esriImageryUrl(course.lat as number, course.lon as number));
                            }}
                          >
                            <Text style={styles.inlineLink}>{t('golfCourseAdminPage.openInEsri')}</Text>
                          </Pressable>
                          <Pressable
                            testID="golf-course-admin-open-in-osm"
                            accessibilityRole="link"
                            hitSlop={8}
                            onPress={(event) => {
                              event.stopPropagation();
                              void Linking.openURL(osmUrl(course));
                            }}
                          >
                            <Text style={styles.inlineLink}>{t('golfCourseAdminPage.openInOsm')}</Text>
                          </Pressable>
                        </View>
                      ) : null}

                      <View style={styles.analysisRow}>
                        <View
                          style={[
                            styles.analysisBadge,
                            course.analysisState === 'analysed'
                              ? styles.analysisBadgeGood
                              : course.analysisState === 'abandoned' || course.analysisState === 'disabled'
                                ? styles.analysisBadgeBad
                                : styles.analysisBadgeWarn,
                          ]}
                        >
                          <Text testID="golf-course-admin-analysis-badge" style={styles.analysisBadgeText}>
                            {course.analysisState
                              ? t(`courseAnalysis.status.state.${course.analysisState}`)
                              : t('golfCourseAdminPage.analysisUnknown')}
                          </Text>
                        </View>
                        <Pressable
                          testID="golf-course-admin-analyze-button"
                          accessibilityRole="button"
                          disabled={
                            analyzeRowState[course.id]?.status === 'running' || course.analysisState === 'noGeodata'
                          }
                          style={[
                            styles.analyzeButton,
                            (analyzeRowState[course.id]?.status === 'running' ||
                              course.analysisState === 'noGeodata') &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            startAnalyze(course);
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t(
                              analyzeRowState[course.id]?.status === 'running'
                                ? 'courseAnalysis.status.running'
                                : 'courseAnalysis.status.run',
                            )}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID="golf-course-admin-flyover-button"
                          accessibilityRole="button"
                          disabled={course.analysisState === 'noGeodata' || course.flyableHoles === 0}
                          aria-disabled={course.analysisState === 'noGeodata' || course.flyableHoles === 0}
                          style={[
                            styles.analyzeButton,
                            (course.analysisState === 'noGeodata' || course.flyableHoles === 0) &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            openFlyoverModal(course);
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t('golfCourseAdminPage.flyoverButton')}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID="golf-course-admin-osm-refresh-button"
                          accessibilityRole="button"
                          disabled={
                            !course.osmType || !course.osmId || osmRefreshState[course.id]?.status === 'running'
                          }
                          style={[
                            styles.analyzeButton,
                            (!course.osmType || !course.osmId || osmRefreshState[course.id]?.status === 'running') &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            void refreshOsm(course);
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t(
                              osmRefreshState[course.id]?.status === 'running'
                                ? 'golfCourseAdminPage.osmRefreshRunning'
                                : 'golfCourseAdminPage.osmRefreshButton',
                            )}
                          </Text>
                        </Pressable>
                        {/* Acts on the club, not the course, which is why it is disabled without a
                            club id: the row is simply where an admin meets a club. Two courses of
                            one club therefore drive the same button, and both cards report it. */}
                        <Pressable
                          testID="golf-course-admin-club-enrichment-button"
                          accessibilityRole="button"
                          disabled={!course.clubId || clubEnrichment[course.clubId]?.status === 'running'}
                          style={[
                            styles.analyzeButton,
                            (!course.clubId || clubEnrichment[course.clubId]?.status === 'running') &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={(event) => {
                            event.stopPropagation();
                            if (course.clubId) {
                              onFetchClubDetails(course.clubId);
                            }
                          }}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t(
                              course.clubId && clubEnrichment[course.clubId]?.status === 'running'
                                ? 'golfCourseAdminPage.clubEnrichmentRunning'
                                : 'golfCourseAdminPage.clubEnrichmentButton',
                            )}
                          </Text>
                        </Pressable>
                      </View>
                      {((state) => {
                        if (!state) return null;
                        if (state.status === 'error') {
                          return (
                            <Text testID="golf-course-admin-club-enrichment-status" style={styles.errorText}>
                              {t(
                                state.code === 'courseAdmin.clubEnrichment.disabled'
                                  ? 'golfCourseAdminPage.clubEnrichmentDisabled'
                                  : 'golfCourseAdminPage.clubEnrichmentFailed',
                              )}
                            </Text>
                          );
                        }
                        if (state.status !== 'done') return null;
                        // A run that changed nothing is the common case early on, and the reason
                        // is the whole message: "no website" points at the club list, "http 403"
                        // points at the club's own server.
                        return state.enriched ? (
                          <Text testID="golf-course-admin-club-enrichment-status" style={styles.analyzeQueuedText}>
                            {t('golfCourseAdminPage.clubEnrichmentDone', {
                              logo: t(state.gotLogo ? 'golfCourseAdminPage.yes' : 'golfCourseAdminPage.no'),
                              description: t(
                                state.gotDescription ? 'golfCourseAdminPage.yes' : 'golfCourseAdminPage.no',
                              ),
                            })}
                          </Text>
                        ) : (
                          <Text testID="golf-course-admin-club-enrichment-status" style={styles.errorText}>
                            {t('golfCourseAdminPage.clubEnrichmentSkipped', {
                              reason: state.skippedReason ?? t('golfCourseAdminPage.clubEnrichmentNoReason'),
                            })}
                          </Text>
                        );
                      })(course.clubId ? clubEnrichment[course.clubId] : undefined)}
                      {((refresh) =>
                        refresh?.status === 'done' ? (
                          <Text testID="golf-course-admin-osm-refresh-status" style={styles.analyzeQueuedText}>
                            {t('golfCourseAdminPage.osmRefreshResult', {
                              written: String(refresh.written),
                              updated: String(refresh.updated),
                              removed: String(refresh.removed),
                              unchanged: String(refresh.unchanged),
                            })}
                          </Text>
                        ) : null)(osmRefreshState[course.id])}
                      {((refresh) =>
                        refresh?.status === 'failed'
                          ? ((failure) => (
                              <Text testID="golf-course-admin-osm-refresh-status" style={styles.errorText}>
                                {t(failure.key, failure.params)}
                              </Text>
                            ))(osmRefreshFailure(refresh.httpStatus))
                          : null)(osmRefreshState[course.id])}
                      {analyzeRowState[course.id]?.status === 'queued' ? (
                        <Text testID="golf-course-admin-analyze-status" style={styles.analyzeQueuedText}>
                          {t('courseAnalysis.status.queued')}
                        </Text>
                      ) : ((analyze) =>
                          analyze?.status === 'failed' ? (
                            <Text testID="golf-course-admin-analyze-status" style={styles.errorText}>
                              {t(analyzeFailureKey(analyze.code))}
                            </Text>
                          ) : null)(analyzeRowState[course.id])}
                      {course.analysisState === 'noGeodata' ? (
                        <Text testID="golf-course-admin-no-geodata-hint" style={styles.courseCardOsm}>
                          {t('golfCourseAdminPage.actionNoGeodata')}
                        </Text>
                      ) : course.flyableHoles === 0 ? (
                        // Why the flyover button above is greyed out: no hole has both a tee and a
                        // green, so a render would refuse. The no-geodata hint already covers the
                        // wider case, so only one of the two ever shows.
                        <Text testID="golf-course-admin-nothing-to-fly-hint" style={styles.courseCardOsm}>
                          {t('golfCourseAdminPage.flyoverNothingToFly')}
                        </Text>
                      ) : null}

                      {/* What the detail popup used to hold. On the card each one is a press
                          away rather than two, and the row above already carries the actions
                          that change this course — these open what there is to look at. */}
                      <View style={styles.analysisRow}>
                        <Pressable
                          testID={`golf-course-admin-scorecard-${course.id}`}
                          accessibilityRole="button"
                          style={styles.analyzeButton}
                          onPress={() =>
                            router.push(
                              `/course-scorecard?courseId=${encodeURIComponent(course.id)}`,
                            )
                          }
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t('golfCourseAdminPage.scorecardButton')}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID={`golf-course-admin-hole-map-${course.id}`}
                          accessibilityRole="button"
                          style={styles.analyzeButton}
                          onPress={() => setHoleMapCourse(course)}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t('golfCourseAdminPage.holeMapButton')}
                          </Text>
                        </Pressable>
                        {/* Recording stakes is the opposite of marking on the web: it needs the
                            phone's GPS and compass, standing next to the stake. */}
                        {Platform.OS !== 'web' ? (
                          <Pressable
                            testID={`golf-course-admin-stakes-${course.id}`}
                            accessibilityRole="button"
                            style={styles.analyzeButton}
                            onPress={() =>
                              router.push(
                                `/course-stakes?courseId=${encodeURIComponent(course.id)}&name=${encodeURIComponent(course.name)}&holes=${course.holesCount}`,
                              )
                            }
                          >
                            <Text style={styles.analyzeButtonText}>
                              {t('golfCourseAdminPage.stakesButton')}
                            </Text>
                          </Pressable>
                        ) : null}
                        <Pressable
                          testID={`golf-course-admin-analysis-status-${course.id}`}
                          accessibilityRole="button"
                          style={styles.analyzeButton}
                          onPress={() =>
                            router.push(
                              `/course-analysis-status?courseId=${encodeURIComponent(course.id)}&name=${encodeURIComponent(course.name)}`,
                            )
                          }
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t('golfCourseAdminPage.analysisStatusButton')}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID={`golf-course-admin-analysis-features-${course.id}`}
                          accessibilityRole="button"
                          style={styles.analyzeButton}
                          onPress={() =>
                            router.push(
                              `/course-analysis?courseId=${encodeURIComponent(course.id)}`,
                            )
                          }
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t('golfCourseAdminPage.analysisFeaturesButton')}
                          </Text>
                        </Pressable>
                        <Pressable
                          testID={`golf-course-admin-gis-import-${course.id}`}
                          accessibilityRole="button"
                          disabled={gisImportState[course.id]?.status === 'running'}
                          style={[
                            styles.analyzeButton,
                            gisImportState[course.id]?.status === 'running' &&
                              styles.actionButtonDisabled,
                          ]}
                          onPress={() => void importGis(course)}
                        >
                          <Text style={styles.analyzeButtonText}>
                            {t(
                              gisImportState[course.id]?.status === 'running'
                                ? 'golfCourseAdminPage.gisImportRunning'
                                : 'golfCourseAdminPage.gisImportButton',
                            )}
                          </Text>
                        </Pressable>
                      </View>

                      {/* Judged on what was written, not on whether the service got as far as
                          recording an outcome: it records one for "no course on the page
                          matched" and for "every row belongs to a person" too, and reporting
                          either of those as "0 holes imported" claims an import that never
                          happened while hiding the sentence that says why. */}
                      {((gis) =>
                        gis?.status === 'done' ? (
                          gis.holes > 0 || gis.tees > 0 ? (
                            <Text
                              testID={`golf-course-admin-gis-import-status-${course.id}`}
                              style={styles.analyzeQueuedText}
                            >
                              {t('golfCourseAdminPage.gisImportResult', {
                                holes: String(gis.holes),
                                tees: String(gis.tees),
                              })}
                            </Text>
                          ) : (
                            <>
                              <Text
                                testID={`golf-course-admin-gis-import-status-${course.id}`}
                                style={styles.courseCardOsm}
                              >
                                {t('golfCourseAdminPage.gisImportNothing', {
                                  reason: gis.reason ?? t('golfCourseAdminPage.gisImportNoReason'),
                                })}
                              </Text>
                              {/* Which club page it read. "No course matched" means one thing
                                  when the right club was matched and quite another when the
                                  name search landed on a different club. */}
                              {gis.clubSlug ? (
                                <Text style={styles.courseCardOsm}>
                                  {t('golfCourseAdminPage.gisImportClub', { club: gis.clubSlug })}
                                </Text>
                              ) : null}
                            </>
                          )
                        ) : gis?.status === 'failed' ? (
                          <Text
                            testID={`golf-course-admin-gis-import-status-${course.id}`}
                            style={styles.errorText}
                          >
                            {t(
                              gis.httpStatus === 403
                                ? 'golfCourseAdminPage.gisImportForbidden'
                                : 'golfCourseAdminPage.gisImportFailed',
                            )}
                          </Text>
                        ) : null)(gisImportState[course.id])}

                      {course.osmType && course.osmId ? (
                        <Text style={styles.courseCardOsm}>
                          {t('golfCourseAdminPage.courseCardOsm', {
                            osmType: course.osmType,
                            osmId: String(course.osmId),
                          })}
                        </Text>
                      ) : null}

                      <Text testID="golf-course-admin-osm-features" style={styles.courseCardOsm}>
                        {t('golfCourseAdminPage.courseCardOsmFeatures', {
                          greens: String(course.osmFeatures.greens),
                          holes: String(course.osmFeatures.holes),
                          bunkers: String(course.osmFeatures.bunkers),
                          fairways: String(course.osmFeatures.fairways),
                        })}
                      </Text>

                      {course.scorecardImport ? (
                        <Text
                          testID="golf-course-admin-scorecard-import"
                          style={styles.courseCardOsm}
                        >
                          {t('golfCourseAdminPage.courseCardScorecardImport', {
                            holes: String(course.scorecardImport.holes),
                            tees: String(course.scorecardImport.tees),
                            date: formatImportDate(course.scorecardImport.importedAtUtc),
                          })}
                        </Text>
                      ) : null}

                      {course.flyover ? (
                        <Text
                          testID="golf-course-admin-flyover-state"
                          style={
                            course.flyover.rendered
                              ? styles.flyoverStateRendered
                              : course.flyover.latestStatus === 'failed'
                                ? styles.errorText
                                : styles.courseCardOsm
                          }
                        >
                          {course.flyover.rendered
                            ? t('golfCourseAdminPage.flyoverStateRendered', {
                                holes: String(course.flyover.renderedHoles),
                              })
                            : course.flyover.latestStatus === 'failed'
                              ? t('golfCourseAdminPage.flyoverStateFailed')
                              : t('golfCourseAdminPage.flyoverStateQueued')}
                        </Text>
                      ) : null}
                      {course.osmExtract ? (
                        <Text
                          testID="golf-course-admin-osm-extract-outcome"
                          style={course.osmExtract.outcome === 'failed' ? styles.errorText : styles.courseCardOsm}
                        >
                          {t('golfCourseAdminPage.osmExtractRow', {
                            outcome: extractOutcomeLabel(course.osmExtract.outcome),
                          })}
                        </Text>
                      ) : null}
                    </View>
                  ))}
                </View>
              )
            ) : null}

            {coursesState.status === 'loaded' && currentPage.total > 0 ? (
              <View style={styles.pagerRow}>
                <Pressable
                  testID="golf-course-admin-page-prev"
                  accessibilityRole="button"
                  disabled={currentPage.page <= 1}
                  style={[styles.pagerButton, currentPage.page <= 1 && styles.actionButtonDisabled]}
                  onPress={() => updateFilters({ page: currentPage.page - 1 })}
                >
                  <Text style={styles.analyzeButtonText}>{t('golfCourseAdminPage.previousPage')}</Text>
                </Pressable>
                <Text testID="golf-course-admin-page-status" style={styles.progressText}>
                  {t('golfCourseAdminPage.pageRange', {
                    from: String(currentPage.from),
                    to: String(currentPage.to),
                    total: String(currentPage.total),
                    page: String(currentPage.page),
                    pages: String(currentPage.pageCount),
                  })}
                </Text>
                <Pressable
                  testID="golf-course-admin-page-next"
                  accessibilityRole="button"
                  disabled={currentPage.page >= currentPage.pageCount}
                  style={[
                    styles.pagerButton,
                    currentPage.page >= currentPage.pageCount && styles.actionButtonDisabled,
                  ]}
                  onPress={() => updateFilters({ page: currentPage.page + 1 })}
                >
                  <Text style={styles.analyzeButtonText}>{t('golfCourseAdminPage.nextPage')}</Text>
                </Pressable>
              </View>
            ) : null}

          </>
        )}
      </ScrollView>

      <Modal
        visible={visibilityPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setVisibilityPickerOpen(false)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setVisibilityPickerOpen(false)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>
                {t('golfCourseAdminPage.visibilityPickerTitle', { count: String(selectedIds.length) })}
              </Text>
              <Pressable
                testID="golf-course-admin-visibility-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setVisibilityPickerOpen(false)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            {SETTABLE_VISIBILITIES.map((visibility) => (
              <Pressable
                key={visibility}
                testID={`golf-course-admin-visibility-${visibility}`}
                accessibilityRole="button"
                style={styles.pickerOption}
                onPress={() => setVisibilityForSelected(visibility)}
              >
                <Text style={styles.pickerOptionText}>
                  {t(`golfCourseAdminPage.visibility${visibility[0].toUpperCase()}${visibility.slice(1)}`)}
                </Text>
              </Pressable>
            ))}
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={listCountryPickerOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setListCountryPickerOpen(false)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setListCountryPickerOpen(false)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>{t('golfCourseAdminPage.countryLabel')}</Text>
              <Pressable
                testID="golf-course-admin-list-country-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setListCountryPickerOpen(false)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            <ScrollView style={styles.countryList}>
              {[{ code: ALL_COUNTRIES, name: t('golfCourseAdminPage.countryAll') }, ...COURSE_ADMIN_COUNTRIES].map(
                (country) => (
                  <Pressable
                    key={country.code}
                    testID={`golf-course-admin-list-country-${country.code}`}
                    style={styles.pickerOption}
                    onPress={() => {
                      updateFilters({ countryCode: country.code, page: 1 });
                      setListCountryPickerOpen(false);
                    }}
                  >
                    <Text style={styles.pickerOptionText}>{country.name}</Text>
                  </Pressable>
                ),
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal
        visible={flyoverCommandCourse !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setFlyoverCommandCourse(null)}
      >
        <Pressable
          style={[styles.pickerOverlay, styles.osmModalOverlay]}
          onPress={() => setFlyoverCommandCourse(null)}
        >
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>
                {t('golfCourseAdminPage.flyoverModalTitle', {
                  name: flyoverCommandCourse?.name ?? '',
                })}
              </Text>
              <Pressable
                testID="golf-course-admin-flyover-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={() => setFlyoverCommandCourse(null)}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>
            <View style={styles.osmModalBody}>
              <Text style={styles.paragraph}>{t('golfCourseAdminPage.flyoverWorkerExplainer')}</Text>
              {flyoverCommandCourse ? (
                <FlyoverJobStatusBlock
                  status={flyoverStatus[flyoverCommandCourse.id]}
                  request={flyoverRequest[flyoverCommandCourse.id]}
                  onRender={() => startFlyoverRender(flyoverCommandCourse.id)}
                />
              ) : null}
              <Pressable
                testID="golf-course-admin-flyover-toggle-command"
                accessibilityRole="button"
                onPress={() => setFlyoverShowCommand((prev) => !prev)}
              >
                <Text style={styles.linkText}>
                  {flyoverShowCommand
                    ? t('golfCourseAdminPage.flyoverHideCommand')
                    : t('golfCourseAdminPage.flyoverShowCommand')}
                </Text>
              </Pressable>
              {flyoverShowCommand ? (
                <>
              <Text style={styles.paragraph}>{t('golfCourseAdminPage.flyoverExplainer')}</Text>
              <ScrollView horizontal style={styles.flyoverCommandBox}>
                <Text
                  testID="golf-course-admin-flyover-command"
                  selectable
                  style={styles.flyoverCommandText}
                >
                  {flyoverCommandCourse ? flyoverCommandFor(flyoverCommandCourse.id) : ''}
                </Text>
              </ScrollView>
              {Platform.OS === 'web' ? (
                <View style={styles.flyoverCopyRow}>
                  <Pressable
                    testID="golf-course-admin-flyover-copy"
                    accessibilityRole="button"
                    style={styles.analyzeButton}
                    onPress={() =>
                      flyoverCommandCourse && copyFlyoverCommand(flyoverCommandCourse.id)
                    }
                  >
                    <Text style={styles.analyzeButtonText}>
                      {t('golfCourseAdminPage.flyoverCopy')}
                    </Text>
                  </Pressable>
                  {flyoverCommandCopied ? (
                    <Text testID="golf-course-admin-flyover-copied" style={styles.statusText}>
                      {t('golfCourseAdminPage.flyoverCopied')}
                    </Text>
                  ) : null}
                </View>
              ) : null}
                </>
              ) : null}
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      <Modal visible={modalOpen} transparent animationType="fade" onRequestClose={closeModal}>
        <Pressable style={[styles.pickerOverlay, styles.osmModalOverlay]} onPress={closeModal}>
          <Pressable style={styles.osmModalCard} onPress={(event) => event.stopPropagation()}>
            <View style={styles.osmModalHeader}>
              <Text style={styles.osmPickerTitle}>{t('golfCourseAdminPage.modalTitle')}</Text>
              <Pressable
                testID="golf-course-admin-modal-close"
                accessibilityRole="button"
                accessibilityLabel={t('golfCourseAdminPage.close')}
                hitSlop={8}
                style={styles.osmModalClose}
                onPress={closeModal}
              >
                <Ionicons name="close" size={20} color={appColors.text.primary} />
              </Pressable>
            </View>

            <View style={styles.osmModalBody}>
              {countryPickerOpen ? (
                <ScrollView style={styles.countryList}>
                  {COURSE_ADMIN_COUNTRIES.map((country) => (
                    <Pressable
                      key={country.code}
                      testID={`golf-course-admin-country-${country.code}`}
                      style={styles.pickerOption}
                      onPress={() => {
                        setCountryCode(country.code);
                        setCountryPickerOpen(false);
                        fetchImportOptions(country.code);
                      }}
                    >
                      <Text style={styles.pickerOptionText}>{country.name}</Text>
                    </Pressable>
                  ))}
                </ScrollView>
              ) : importState.status === 'done' ? (
                <>
                  {importState.authoritativeSource ? (
                    <Text testID="golf-course-admin-source-result" style={styles.statusText}>
                      {importState.authoritativeSource.result
                        ? t('golfCourseAdminPage.resultFromSource', {
                            source: importState.authoritativeSource.source,
                            attached: String(importState.authoritativeSource.result.boundariesAttached),
                            discovered: String(importState.authoritativeSource.result.discovered),
                          })
                        : t('golfCourseAdminPage.sourceUnavailable', {
                            source: importState.authoritativeSource.source,
                          })}
                    </Text>
                  ) : null}
                  <Text style={styles.statusText}>
                    {t('golfCourseAdminPage.resultSummary', {
                      imported: String(importState.importedCount),
                      skipped: String(importState.skippedCount),
                    })}
                  </Text>
                  {importState.failedCount > 0 ? (
                    <Text style={styles.errorText}>
                      {t('golfCourseAdminPage.resultFailed', { failed: String(importState.failedCount) })}
                    </Text>
                  ) : null}
                  {importState.unnamedCount > 0 ? (
                    <Text testID="golf-course-admin-unnamed-skipped" style={styles.statusText}>
                      {t('golfCourseAdminPage.resultUnnamedSkipped', {
                        unnamed: String(importState.unnamedCount),
                      })}
                    </Text>
                  ) : null}
                  {importState.imported.length > 0 ? (
                    <>
                      <Text style={styles.importedListTitle}>{t('golfCourseAdminPage.importedListTitle')}</Text>
                      <ScrollView style={styles.importedList}>
                        {importState.imported.map((course) => (
                          <Text key={course.courseId} style={styles.importedListItem}>
                            {course.name}
                          </Text>
                        ))}
                      </ScrollView>
                    </>
                  ) : null}
                  <Pressable
                    testID="golf-course-admin-import-again"
                    accessibilityRole="button"
                    style={styles.scanButton}
                    onPress={() => setImportState({ status: 'idle' })}
                  >
                    <Text style={styles.scanButtonText}>{t('golfCourseAdminPage.importButton')}</Text>
                  </Pressable>
                </>
              ) : (
                <>
                  <Pressable
                    testID="golf-course-admin-country-row"
                    style={styles.countryRow}
                    onPress={() => setCountryPickerOpen(true)}
                  >
                    <Text style={styles.officialLabel}>{t('golfCourseAdminPage.countryLabel')}</Text>
                    <Text style={styles.pickerOptionText}>{selectedCountry.name}</Text>
                  </Pressable>

                  {importOptions.status === 'loaded'
                    ? importOptions.sources.map((source) => {
                        const checked = selectedSources.includes(source.key);
                        return (
                          <Pressable
                            key={source.key}
                            testID={`golf-course-admin-source-${source.key}`}
                            accessibilityRole="checkbox"
                            accessibilityState={{ checked }}
                            style={styles.sourceRow}
                            disabled={importState.status === 'importing'}
                            onPress={() =>
                              setSelectedSources((prev) =>
                                checked ? prev.filter((key) => key !== source.key) : [...prev, source.key],
                              )
                            }
                          >
                            <Ionicons
                              name={checked ? 'checkbox' : 'square-outline'}
                              size={20}
                              color={appColors.text.primary}
                            />
                            <Text style={styles.pickerOptionText}>{source.displayName}</Text>
                            {typeof source.alreadyImported === 'number' ? (
                              <Text style={styles.sourceCountText}>
                                {t('golfCourseAdminPage.sourceAlreadyImported', {
                                  count: String(source.alreadyImported),
                                })}
                              </Text>
                            ) : null}
                          </Pressable>
                        );
                      })
                    : null}

                  <Pressable
                    testID="golf-course-admin-import"
                    accessibilityRole="button"
                    style={[
                      styles.scanButton,
                      (importState.status === 'importing'
                        || (importOptions.status === 'loaded' && selectedSources.length === 0))
                        && styles.actionButtonDisabled,
                    ]}
                    disabled={
                      importState.status === 'importing'
                      || (importOptions.status === 'loaded' && selectedSources.length === 0)
                    }
                    onPress={onImport}
                  >
                    {importState.status === 'importing' ? (
                      <ActivityIndicator color={appColors.text.primary} />
                    ) : (
                      <Ionicons name="cloud-download-outline" size={18} color={appColors.text.primary} />
                    )}
                    <Text style={styles.scanButtonText}>
                      {importState.status === 'importing'
                        ? t('golfCourseAdminPage.importing')
                        : t('golfCourseAdminPage.importButton')}
                    </Text>
                  </Pressable>

                  {importState.status === 'importing' && importState.progress ? (
                    <Text testID="golf-course-admin-progress" style={styles.progressText}>
                      {describeProgress(importState.progress, t)}
                    </Text>
                  ) : null}

                  {importState.status === 'error' ? (
                    <Text testID="golf-course-admin-error" style={styles.errorText}>
                      {t('golfCourseAdminPage.error')}
                    </Text>
                  ) : null}

                  <Text style={styles.detailSectionTitle}>
                    {t('golfCourseAdminPage.featureImportTitle')}
                  </Text>
                  <Text style={styles.courseCardMeta}>{t('golfCourseAdminPage.featureImportHint')}</Text>
                  <Pressable
                    testID="golf-course-admin-feature-import"
                    accessibilityRole="button"
                    style={[
                      styles.scanButton,
                      (featureImportState.status === 'running' || extractRunning) && styles.actionButtonDisabled,
                    ]}
                    disabled={featureImportState.status === 'running' || extractRunning}
                    onPress={onImportFeatures}
                  >
                    {featureImportState.status === 'running' || extractRunning ? (
                      <ActivityIndicator color={appColors.text.primary} />
                    ) : (
                      <Ionicons name="golf-outline" size={18} color={appColors.text.primary} />
                    )}
                    <Text style={styles.scanButtonText}>
                      {extractRunning
                        ? t('golfCourseAdminPage.featureImportQueuedRunning')
                        : featureImportState.status === 'running'
                          ? t('golfCourseAdminPage.featureImportRunning')
                          : t('golfCourseAdminPage.featureImportButton')}
                    </Text>
                  </Pressable>

                  {featureImportState.status === 'queued' && !latestExtract ? (
                    <Text testID="golf-course-admin-feature-import-queued" style={styles.progressText}>
                      {t('golfCourseAdminPage.featureImportQueued', {
                        courses: String(featureImportState.coursesRequested),
                      })}
                    </Text>
                  ) : null}

                  {latestExtract ? (
                    <>
                      <Text testID="golf-course-admin-extract-status" style={styles.statusText}>
                        {latestExtract.status === 'queued'
                          ? t('golfCourseAdminPage.extractQueued', {
                              country: latestExtract.countryCode,
                              courses: String(latestExtract.coursesRequested),
                            })
                          : latestExtract.status === 'succeeded'
                            ? t('golfCourseAdminPage.extractDone', {
                                country: latestExtract.countryCode,
                                applied: String(latestExtract.coursesApplied),
                                courses: String(latestExtract.coursesWithFeatures),
                                written: String(latestExtract.featuresWritten),
                                updated: String(latestExtract.featuresUpdated),
                              })
                            : t('golfCourseAdminPage.extractFailed', {
                                country: latestExtract.countryCode,
                                error: latestExtract.error ?? '',
                              })}
                      </Text>
                      {/* "208 of 467 updated" reads as "259 still to do", and after a country has
                          been imported once almost none of them are — most come back unchanged.
                          This line is what answers the question that number provokes. */}
                      {latestExtract.status === 'succeeded' && latestExtract.outcomes ? (
                        <Text testID="golf-course-admin-extract-outcomes" style={styles.statusText}>
                          {t('golfCourseAdminPage.extractOutcomes', {
                            current: String(latestExtract.outcomes.alreadyCurrent),
                            noOsm: String(latestExtract.outcomes.nothingInOsm),
                            remaining: String(latestExtract.outcomes.remaining),
                          })}
                        </Text>
                      ) : null}
                      {latestExtract.status === 'queued' && !extracts.workerOnline ? (
                        <Text testID="golf-course-admin-extract-no-worker" style={styles.errorText}>
                          {t('golfCourseAdminPage.extractNoWorker')}
                        </Text>
                      ) : null}
                      {latestExtract.status !== 'queued' ? (
                        <Pressable
                          testID="golf-course-admin-extract-courses-toggle"
                          accessibilityRole="button"
                          onPress={() => toggleExtractCourses(latestExtract.id)}
                        >
                          <Text style={styles.linkText}>
                            {extractCourses.status !== 'idle' && extractCourses.jobId === latestExtract.id
                              ? t('golfCourseAdminPage.extractCoursesHide')
                              : t('golfCourseAdminPage.extractCoursesShow')}
                          </Text>
                        </Pressable>
                      ) : null}
                      {extractCourses.status === 'loaded' && extractCourses.jobId === latestExtract.id
                        ? groupExtractCourses(extractCourses.courses).map((group) => (
                            <View key={group.outcome}>
                              <Pressable
                                testID={`golf-course-admin-extract-outcome-${group.outcome}`}
                                accessibilityRole="button"
                                onPress={() =>
                                  setOpenOutcomes((prev) =>
                                    prev.includes(group.outcome)
                                      ? prev.filter((outcome) => outcome !== group.outcome)
                                      : [...prev, group.outcome],
                                  )
                                }
                              >
                                <Text style={styles.statusText}>
                                  {t('golfCourseAdminPage.extractOutcomeGroup', {
                                    outcome: extractOutcomeLabel(group.outcome),
                                    count: String(group.courses.length),
                                  })}
                                </Text>
                              </Pressable>
                              {openOutcomes.includes(group.outcome)
                                ? group.courses.map((row) => (
                                    <Text
                                      key={row.courseId}
                                      testID="golf-course-admin-extract-course"
                                      style={styles.courseCardOsm}
                                    >
                                      {row.detail
                                        ? `${row.courseName ?? row.courseId} — ${row.detail}`
                                        : (row.courseName ?? row.courseId)}
                                    </Text>
                                  ))
                                : null}
                            </View>
                          ))
                        : null}
                      {extractCourses.status === 'error' && extractCourses.jobId === latestExtract.id ? (
                        <Text style={styles.errorText}>{t('golfCourseAdminPage.extractCoursesError')}</Text>
                      ) : null}
                    </>
                  ) : null}

                  {featureImportState.status === 'error' ? (
                    <Text testID="golf-course-admin-feature-import-error" style={styles.errorText}>
                      {featureImportState.code === 'courseAdmin.osm.featureImport.no_extract'
                        ? t('golfCourseAdminPage.featureImportNoExtract')
                        : featureImportState.code === 'courseAdmin.osm.featureImport.no_courses'
                          ? t('golfCourseAdminPage.featureImportNoCourses')
                          : t('golfCourseAdminPage.featureImportError')}
                    </Text>
                  ) : null}
                </>
              )}
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Marking positions is offered on the web only. It is precision mouse work on
          satellite imagery — pointing at a tee box a few metres across — and the phone this
          app usually runs on is the wrong tool for it, not merely a smaller one. */}
      <CourseHoleMapModal
        visible={holeMapCourse !== null}
        onClose={() => setHoleMapCourse(null)}
        apiBaseUrl={apiBaseUrl}
        token={user?.token}
        courseId={holeMapCourse?.id ?? null}
        holeNumbers={
          holeMapCourse ? Array.from({ length: holeMapCourse.holesCount }, (_, index) => index + 1) : []
        }
        editable={isAdmin && Platform.OS === 'web'}
        openedFor={holeMap ? 'evaluation' : undefined}
      />
    </>
  );
}

/**
 * The batch-worker path of the flyover modal: whether a worker is switched on, how the last
 * render went, and the button that queues a new one. Rendering happens on a machine outside
 * the cloud that may be off for days, so "queued" with no worker online is a normal state the
 * admin needs to be told about, not an error.
 */
function FlyoverJobStatusBlock({
  status,
  request,
  onRender,
}: {
  status: FlyoverStatus | null | undefined;
  request: { state: FlyoverRequestState; code?: string } | undefined;
  onRender: () => void;
}) {
  const { t } = useI18n();
  const requesting = request?.state === 'requesting';
  const job = status?.latestJob ?? null;
  // Nothing to fly = the render endpoint would refuse (409 no_holes), so don't offer it.
  // Unknown (older geodata, or status not loaded yet) leaves the button enabled.
  const nothingToFly = status?.flyableHoles === 0;
  return (
    <View style={styles.flyoverJobBlock}>
      {status ? (
        <Text
          testID="golf-course-admin-flyover-worker"
          style={status.workerOnline ? styles.statusText : styles.errorText}
        >
          {status.workerOnline
            ? t('golfCourseAdminPage.flyoverWorkerOnline')
            : t('golfCourseAdminPage.flyoverWorkerOffline')}
        </Text>
      ) : null}
      {nothingToFly ? (
        <Text testID="golf-course-admin-flyover-nothing-to-fly" style={styles.errorText}>
          {t('golfCourseAdminPage.flyoverNothingToFly')}
        </Text>
      ) : null}
      {job ? (
        <Text testID="golf-course-admin-flyover-last-job" style={styles.progressText}>
          {t('golfCourseAdminPage.flyoverLastJob', {
            status: t(`golfCourseAdminPage.flyoverJobStatus.${job.status}`),
            rendered: job.renderedHoles ?? 0,
            skipped: job.skippedHoles ?? 0,
          })}
          {job.workerId ? ` · ${job.workerId}` : ''}
          {job.error ? ` · ${job.error}` : ''}
        </Text>
      ) : null}
      <View style={styles.flyoverCopyRow}>
        <Pressable
          testID="golf-course-admin-flyover-render"
          accessibilityRole="button"
          disabled={requesting || nothingToFly}
          aria-disabled={requesting || nothingToFly}
          style={[styles.analyzeButton, (requesting || nothingToFly) && styles.actionButtonDisabled]}
          onPress={onRender}
        >
          <Text style={styles.analyzeButtonText}>
            {requesting
              ? t('golfCourseAdminPage.flyoverRequesting')
              : t('golfCourseAdminPage.flyoverRender')}
          </Text>
        </Pressable>
        {request?.state === 'queued' ? (
          <Text testID="golf-course-admin-flyover-queued" style={styles.statusText}>
            {t('golfCourseAdminPage.flyoverQueued')}
          </Text>
        ) : request?.state === 'failed' ? (
          <Text testID="golf-course-admin-flyover-error" style={styles.errorText}>
            {t(flyoverFailureKey(request.code))}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  searchInput: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 14,
  },
  searchResults: {
    gap: 8,
  },
  filterRow: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  filterChipActive: {
    borderColor: appColors.border.accent,
    backgroundColor: appColors.status.successBg,
  },
  filterChipText: {
    color: appColors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
  },
  filterChipTextActive: {
    color: appColors.text.primary,
  },
  listCountryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  osmFilterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  smallCheckbox: {
    width: 16,
    height: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  smallCheckboxMark: {
    color: appColors.text.inverse,
    fontSize: 11,
    fontWeight: '900',
    lineHeight: 13,
  },
  selectionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  selectAllRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  pagerButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  courseCard: {
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 10,
    padding: 12,
    gap: 2,
  },
  courseCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  courseCardName: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '700',
  },
  courseCardMeta: {
    color: appColors.text.secondary,
    fontSize: 12,
  },
  coordinateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  inlineLink: {
    color: appColors.accent.primary,
    fontSize: 12,
    textDecorationLine: 'underline',
  },
  courseCardOsm: {
    color: appColors.text.secondary,
    fontSize: 11,
    marginTop: 2,
  },
  flyoverStateRendered: {
    color: appColors.status.success,
    fontSize: 11,
    marginTop: 2,
  },
  analysisRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  visibilityChipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  visibilityChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panel,
  },
  visibilityChipActive: {
    borderColor: appColors.border.accent,
    backgroundColor: appColors.status.successBg,
  },
  // The visibility chips hold one word each and need no layout; this one pairs an icon with
  // its label, so it lays them out in a row rather than stacking them.
  editChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  visibilityChipText: {
    color: appColors.text.secondary,
    fontSize: 11,
    fontWeight: '700',
  },
  visibilityChipTextActive: {
    color: appColors.text.primary,
  },
  analysisBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  analysisBadgeGood: { backgroundColor: appColors.status.successBg },
  analysisBadgeWarn: { backgroundColor: appColors.status.warningBg },
  analysisBadgeBad: { backgroundColor: appColors.status.dangerBgMuted },
  analysisBadgeText: {
    color: appColors.text.primary,
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  analyzeButton: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
  },
  flyoverJobBlock: {
    marginTop: 8,
    marginBottom: 12,
    gap: 6,
  },
  linkText: {
    color: appColors.accent.primary,
    textDecorationLine: 'underline',
    marginTop: 4,
  },
  flyoverCommandBox: {
    marginTop: 10,
    borderRadius: 8,
    backgroundColor: appColors.background.app,
    padding: 12,
    maxHeight: 260,
  },
  flyoverCommandText: {
    color: appColors.text.positive,
    fontSize: 12,
    fontFamily: Platform.select({ web: 'monospace', ios: 'Menlo', default: 'monospace' }),
  },
  flyoverCopyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 12,
  },
  analyzeButtonText: {
    color: appColors.accent.primary,
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  analyzeQueuedText: {
    color: appColors.accent.primary,
    fontSize: 11,
    marginTop: 2,
  },
  detailSectionTitle: {
    color: appColors.text.primary,
    fontSize: 13,
    fontWeight: '700',
    marginTop: 12,
    marginBottom: 6,
  },
  scanButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: 8,
    paddingVertical: 11,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panelAlt,
  },
  scanButtonText: {
    color: appColors.text.primary,
    fontSize: 15,
    fontWeight: '700',
  },
  actionButtonDisabled: {
    opacity: 0.5,
  },
  pickerOverlay: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.35),
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  osmModalOverlay: {
    backgroundColor: alpha(appColors.neutral.black, 0.6),
  },
  osmModalCard: {
    width: '92%',
    maxWidth: 520,
    maxHeight: '85%',
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.elevated,
    overflow: 'hidden',
    shadowColor: appColors.neutral.black,
    shadowOpacity: 0.45,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 12 },
    elevation: 12,
  },
  osmModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: appColors.background.panelAlt,
  },
  osmModalClose: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  osmModalBody: {
    flexShrink: 1,
    padding: 16,
    gap: 10,
  },
  osmPickerTitle: {
    flex: 1,
    color: appColors.text.primary,
    fontSize: 16,
    fontWeight: '700',
  },
  countryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
  },
  sourceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  sourceCountText: {
    flex: 1,
    textAlign: 'right',
    color: appColors.text.secondary,
    fontSize: 13,
  },
  countryList: {
    maxHeight: 320,
  },
  officialLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: appColors.border.accent,
    backgroundColor: appColors.background.panel,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: appColors.background.interactive,
  },
  checkboxMark: {
    color: appColors.text.inverse,
    fontSize: 15,
    fontWeight: '900',
    lineHeight: 17,
  },
  pickerOption: {
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderColor: alpha(appColors.accent.indigo, 0.6),
  },
  pickerOptionText: {
    color: appColors.text.primary,
    fontSize: 15,
  },
  statusText: {
    color: appColors.text.primary,
  },
  progressText: {
    color: appColors.text.secondary,
    fontSize: 13,
  },
  errorText: {
    color: appColors.status.dangerStrong,
  },
  importedListTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: appColors.text.primary,
    marginTop: 6,
  },
  importedList: {
    maxHeight: 240,
  },
  importedListItem: {
    color: appColors.text.primary,
    fontSize: 13,
    paddingVertical: 4,
  },
});
